import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Calendar, FileText, Wrench, Users, MessageSquare, 
  BarChart3, Check, X, Clock, Eye, Search, KeyRound, Receipt, Plus, Trash2, Send, Download, Euro,
  MessageCircle, Phone, Mail, Droplets, Lock, Unlock, Shield, LogIn, Tag, 
  Banknote, CreditCard, Calculator, Printer, DollarSign, TrendingUp, Package, Edit, Copy, RotateCcw, ArrowRightLeft, Bell, AlertCircle
} from 'lucide-react';

// Import constants and utilities from refactored modules
import { 
  API, 
  STATUS_COLORS as statusColors, 
  STATUS_LABELS as statusLabels,
  SERVICES_BY_CATEGORY
} from '../components/admin/constants';

// Import print utilities
import { 
  downloadInvoice, 
  downloadInvoicePdf,
  downloadQuotePdf,
  printThermalReceipt, 
  printSaleInvoice,
  printClientRequestSheet,
  printProfessionalQuote,
  printDuplicataReceipt,
  printContactMessage,
  printAppointmentClientSheet
} from '../components/admin/printUtils';
import DossierAssignmentTag from '../components/DossierAssignmentTag';
import { sortByTreatment } from '../components/admin/sortUtils';

export default function AdminDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [washBookings, setWashBookings] = useState([]);
  const [washDateFilter, setWashDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [washSlotsOverview, setWashSlotsOverview] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [completingAppointment, setCompletingAppointment] = useState(null);
  const [appointmentPrice, setAppointmentPrice] = useState('');
  // Edition libre RDV admin
  const [editingApptAdmin, setEditingApptAdmin] = useState(null);
  const [apptAdminForm, setApptAdminForm] = useState({});
  const [apptAdminSaving, setApptAdminSaving] = useState(false);
  // Edition libre lavage admin
  const [editingWashAdmin, setEditingWashAdmin] = useState(null);
  const [washAdminForm, setWashAdminForm] = useState({});
  const [washAdminSaving, setWashAdminSaving] = useState(false);
  // Notifications admin (boite de reception)
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: '',
    firstname: '',
    company: '',
    email: '',
    phone: '',
    birth_date: '',
    address: '',
    city: '',
    postal_code: '',
    notes: ''
  });
  const [clientSearch, setClientSearch] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    message_type: 'internal'
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [birthdaySettings, setBirthdaySettings] = useState({
    enabled: true,
    subject: 'Joyeux anniversaire ! 🎂',
    message: 'Bonjour {prenom},\n\nToute l\'équipe du Hub Atelier Auto Moto vous souhaite un très joyeux anniversaire ! 🎉\n\nPour fêter ça, bénéficiez de -10% sur votre prochaine prestation.\n\nÀ très bientôt !'
  });
  const [broadcastForm, setBroadcastForm] = useState({
    subject: '',
    message: '',
    message_type: 'internal',
    filter_type: 'all'
  });
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    user_name: '',
    user_firstname: '',
    user_company: '',
    user_email: '',
    user_phone: '',
    user_address: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    notes: '',
    due_date: ''
  });
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showCreditNoteForm, setShowCreditNoteForm] = useState(false);
  const [creditNoteInvoice, setCreditNoteInvoice] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [professionalQuotes, setProfessionalQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState('appointments'); // État pour l'onglet actif
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null); // Client sélectionné pour voir les détails
  const [replyMessage, setReplyMessage] = useState(null); // Contact message to reply
  const [replyBody, setReplyBody] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [clientHistory, setClientHistory] = useState(null);
  const [clientHistoryLoading, setClientHistoryLoading] = useState(false);
  const [dossiersData, setDossiersData] = useState(null);
  const [dossierFilterCollab, setDossierFilterCollab] = useState('');
  const [dossierFilterType, setDossierFilterType] = useState('');

  const sendReply = async () => {
    if (!replyMessage || !replyBody.trim()) return;
    setReplySending(true);
    try {
      await axios.post(
        `${API}/admin/messages/${replyMessage.id}/reply`,
        { subject: `Re: ${replyMessage.subject}`, body: replyBody },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Réponse envoyée par email');
      setReplyMessage(null);
      setReplyBody('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi réponse');
    } finally {
      setReplySending(false);
    }
  };

  const openClientHistory = async (userId) => {
    setClientHistoryLoading(true);
    setClientHistory({ loading: true });
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/full-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientHistory(res.data);
    } catch (e) {
      toast.error('Erreur chargement historique');
      setClientHistory(null);
    } finally {
      setClientHistoryLoading(false);
    }
  };

  const fetchDossiers = async (collab = '', type = '') => {
    try {
      const params = new URLSearchParams();
      if (collab) params.set('assigned_to', collab);
      if (type) params.set('dossier_type', type);
      const res = await axios.get(`${API}/admin/dossiers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDossiersData(res.data);
    } catch (e) {
      toast.error('Erreur chargement dossiers');
    }
  };
  const [quoteForm, setQuoteForm] = useState({
    client_name: '',
    client_firstname: '',
    client_company: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    vehicle_plate: '',
    vehicle_model: '',
    items: [{ description: '', quantity: 1, unit_price: '', total: 0 }],
    notes: '',
    validity_days: 30,
    from_quote_request_id: null
  });
  // Collaborators and login history (admin only)
  const [loginHistory, setLoginHistory] = useState([]);
  const [pendingBalances, setPendingBalances] = useState({ items: [], summary: { total_to_collect: 0, today_amount: 0, today_count: 0, total_count: 0 } });
  const [collaborators, setCollaborators] = useState([]);
  const isMainAdmin = user?.email === 'admin@lehubatelier.fr';

  // Cash register states
  const [cashSession, setCashSession] = useState(null);
  const [cashSales, setCashSales] = useState([]);
  const [showOpenCashModal, setShowOpenCashModal] = useState(false);
  const [showCloseCashModal, setShowCloseCashModal] = useState(false);
  const [showNewSaleModal, setShowNewSaleModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [cashReportType, setCashReportType] = useState('daily');
  const [cashReport, setCashReport] = useState(null);
  const [initialCash, setInitialCash] = useState('');
  const [finalCash, setFinalCash] = useState('');
  const [priceMode, setPriceMode] = useState('TTC');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSubscriptions, setClientSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [activeServiceCategory, setActiveServiceCategory] = useState('wash');
  // CA Reports states
  const [reportType, setReportType] = useState('daily');
  const [reportData, setReportData] = useState(null);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reportYear, setReportYear] = useState(new Date().getFullYear().toString());
  const [loadingReport, setLoadingReport] = useState(false);
  const [saleForm, setSaleForm] = useState({
    items: [{ description: '', category: 'service', quantity: 1, unit_price: '', total: 0 }],
    register_source: 'comptoir', // 'comptoir' (compte dans la caisse) ou 'internet' (hors caisse, modifiable illimite)
    payment_method: 'cash',
    cash_amount: '',
    card_amount: '',
    check_amount: '',
    check_number: '',
    transfer_amount: '',
    cash_given: '',
    cash_change: 0,
    client_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    vehicle_plate: '',
    vehicle_model: '',
    vehicle_year: '',
    vehicle_color: '',
    notes: ''
  });

  // SERVICES_BY_CATEGORY is now imported from '../components/admin/constants'

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user || (user.role !== 'admin' && user.role !== 'collaborator')) {
      toast.error('Accès réservé aux administrateurs');
      navigate('/');
      return;
    }
    fetchAllData();
    fetchMarketingData();
    fetchCashRegisterData();
    fetchNotifications();
    const notifInterval = setInterval(fetchNotifications, 30000); // refresh toutes les 30s
    if (isMainAdmin) {
      fetchLoginHistory();
      fetchCollaborators();
    }
    return () => clearInterval(notifInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, isMainAdmin, authLoading]);

  const fetchAllData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, apptRes, washRes, quotesRes, msgRes, usersRes, invoicesRes, profQuotesRes, balancesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/appointments`, { headers }),
        axios.get(`${API}/admin/wash/bookings`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/invoices`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/professional-quotes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/pending-balances`, { headers }).catch(() => ({ data: { items: [], summary: { total_to_collect: 0, today_amount: 0, today_count: 0, total_count: 0 } } }))
      ]);
      setStats(statsRes.data);
      setAppointments(apptRes.data);
      setWashBookings(washRes.data);
      setQuotes(quotesRes.data);
      setMessages(msgRes.data);
      setUsers(usersRes.data);
      setInvoices(invoicesRes.data);
      setProfessionalQuotes(profQuotesRes.data);
      setPendingBalances(balancesRes.data || { items: [], summary: { total_to_collect: 0, today_amount: 0, today_count: 0, total_count: 0 } });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Fetch login history (admin only)
  const fetchLoginHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/login-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoginHistory(res.data);
    } catch (error) {
      console.error('Error fetching login history:', error);
    }
  };

  // Fetch collaborators (admin only)
  const fetchCollaborators = async () => {
    try {
      const res = await axios.get(`${API}/admin/collaborators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollaborators(res.data);
    } catch (error) {
      console.error('Error fetching collaborators:', error);
    }
  };

  // ===================== CASH REGISTER FUNCTIONS =====================

  const fetchCashRegisterData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sessionRes, salesRes] = await Promise.all([
        axios.get(`${API}/cash-register/current-session`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/cash-register/sales?limit=50`, { headers }).catch(() => ({ data: [] }))
      ]);
      setCashSession(sessionRes.data);
      setCashSales(salesRes.data);
      // Load daily report by default
      fetchCashReport('daily');
    } catch (error) {
      console.error('Error fetching cash register data:', error);
    }
  };

  // Recherche de client existant
  const searchClients = async (query) => {
    if (query.length < 2) {
      setClientSuggestions([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientSuggestions(res.data);
    } catch (error) {
      console.error('Error searching clients:', error);
    }
  };

  // Sélectionner un client existant
  const selectClient = async (client) => {
    setSelectedClient(client);
    setSaleForm({
      ...saleForm,
      client_id: client.id,
      client_name: client.name || `${client.firstname || ''} ${client.lastname || ''}`.trim(),
      client_email: client.email || '',
      client_phone: client.phone || ''
    });
    setClientSearch('');
    setClientSuggestions([]);
    
    // Check if client has active subscriptions
    try {
      const res = await axios.get(`${API}/admin/users/${client.id}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && res.data.length > 0) {
        setClientSubscriptions(res.data);
      } else {
        setClientSubscriptions([]);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setClientSubscriptions([]);
    }
  };

  // Clear selected client
  const clearSelectedClient = () => {
    setSelectedClient(null);
    setClientSubscriptions([]);
    setSelectedSubscription(null);
    setSaleForm({
      ...saleForm,
      client_id: '',
      client_name: '',
      client_email: '',
      client_phone: ''
    });
  };

  // Use subscription for this sale
  const selectSubscription = (subscription) => {
    if (selectedSubscription?.id === subscription.id) {
      setSelectedSubscription(null);
    } else {
      setSelectedSubscription(subscription);
      toast.info(`Abonnement "${subscription.formula_name}" sélectionné - ${subscription.remaining_uses} utilisations restantes`);
    }
  };

  const fetchCashReport = async (type, date = null) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = `${API}/cash-register/report/${type}`;
      if (date) url += `?date=${date}`;
      const res = await axios.get(url, { headers });
      setCashReport(res.data);
      setCashReportType(type);
    } catch (error) {
      console.error('Error fetching cash report:', error);
    }
  };

  const openCashRegister = async () => {
    if (!initialCash || parseFloat(initialCash) < 0) {
      toast.error('Veuillez entrer un montant de fond de caisse valide');
      return;
    }
    try {
      const res = await axios.post(`${API}/cash-register/open`, 
        { initial_cash: parseFloat(initialCash) },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Caisse ouverte');
      setCashSession(res.data.session);
      setShowOpenCashModal(false);
      setInitialCash('');
      fetchCashRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture de la caisse');
    }
  };

  // Fonction pour télécharger la fiche client avec historique
  const downloadClientSheet = async (appointment) => {
    try {
      toast.info('Chargement de l\'historique client...');
      // Récupérer l'historique du client
      const historyRes = await axios.get(`${API}/admin/users/${appointment.user_id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      printAppointmentClientSheet(appointment, historyRes.data);
    } catch (error) {
      console.error('Error fetching client history:', error);
      // Si erreur, imprimer sans historique
      printAppointmentClientSheet(appointment, null);
    }
  };

  const closeCashRegister = async () => {
    if (!finalCash || parseFloat(finalCash) < 0) {
      toast.error('Veuillez entrer le montant final en caisse');
      return;
    }
    try {
      const res = await axios.post(`${API}/cash-register/close`, 
        { final_cash: parseFloat(finalCash) },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      const diff = res.data.difference;
      if (diff === 0) {
        toast.success('Caisse fermée - Comptage exact !');
      } else if (diff > 0) {
        toast.success(`Caisse fermée - Excédent de ${diff.toFixed(2)}€`);
      } else {
        toast.warning(`Caisse fermée - Déficit de ${Math.abs(diff).toFixed(2)}€`);
      }
      setCashSession(null);
      setShowCloseCashModal(false);
      setFinalCash('');
      fetchCashRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la fermeture de la caisse');
    }
  };

  const addSaleItem = () => {
    setSaleForm({
      ...saleForm,
      items: [...saleForm.items, { description: '', category: 'service', quantity: 1, unit_price: '', total: 0 }]
    });
  };

  const removeSaleItem = (index) => {
    if (saleForm.items.length === 1) return;
    const newItems = saleForm.items.filter((_, i) => i !== index);
    setSaleForm({ ...saleForm, items: newItems });
  };

  // Fonction pour parser les prix (accepte virgule ou point)
  const parsePrice = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const cleaned = String(value).replace(',', '.').replace(/[^\d.-]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Fonction pour sélectionner un service prédéfini
  const selectPredefinedService = (index, service) => {
    const newItems = [...saleForm.items];
    newItems[index] = {
      ...newItems[index],
      description: service.description,
      category: service.category,
      unit_price: service.price.toString(),
      total: service.price * newItems[index].quantity
    };
    setSaleForm({ ...saleForm, items: newItems });
  };

  const updateSaleItem = (index, field, value) => {
    const newItems = [...saleForm.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      const qty = field === 'quantity' ? (parseInt(value) || 1) : newItems[index].quantity;
      const price = field === 'unit_price' ? parsePrice(value) : parsePrice(newItems[index].unit_price);
      newItems[index].total = qty * price;
    }
    setSaleForm({ ...saleForm, items: newItems });
  };

  const calculateSaleTotals = () => {
    const itemsTotal = saleForm.items.reduce((sum, item) => sum + (item.total || 0), 0);
    
    if (priceMode === 'TTC') {
      // Les prix sont déjà en TTC
      const total = itemsTotal;
      const subtotalHT = total / 1.2;
      const taxAmount = total - subtotalHT;
      return { subtotalHT, taxAmount, total };
    } else {
      // Les prix sont en HT
      const subtotalHT = itemsTotal;
      const taxAmount = subtotalHT * 0.2;
      const total = subtotalHT + taxAmount;
      return { subtotalHT, taxAmount, total };
    }
  };

  const createSale = async () => {
    const { subtotalHT, taxAmount, total } = calculateSaleTotals();
    
    if (total <= 0) {
      toast.error('Le total doit être supérieur à 0');
      return;
    }

    // Validate required client fields
    if (!saleForm.client_name || !saleForm.client_name.trim()) {
      toast.error('Le nom du client est obligatoire');
      return;
    }
    if (!saleForm.client_email || !saleForm.client_email.trim()) {
      toast.error('L\'email du client est obligatoire');
      return;
    }
    if (!saleForm.client_phone || !saleForm.client_phone.trim()) {
      toast.error('Le téléphone du client est obligatoire');
      return;
    }

    // Validate payment amounts
    const paymentTotal = parsePrice(saleForm.cash_amount) + 
                        parsePrice(saleForm.card_amount) + 
                        parsePrice(saleForm.transfer_amount);
    
    if (saleForm.payment_method === 'mixed' && Math.abs(paymentTotal - total) > 0.01) {
      toast.error('La somme des paiements doit être égale au total');
      return;
    }

    // Calculate cash change
    const cashGiven = parsePrice(saleForm.cash_given);
    const cashChange = saleForm.payment_method === 'cash' && cashGiven > 0 ? cashGiven - total : 0;

    try {
      const saleData = {
        items: saleForm.items.filter(i => i.description && i.total > 0).map(item => ({
          ...item,
          unit_price: parsePrice(item.unit_price)
        })),
        subtotal: subtotalHT,
        tax_rate: 20,
        tax_amount: taxAmount,
        total: total,
        register_source: saleForm.register_source || 'comptoir',
        payment_method: saleForm.payment_method,
        cash_amount: saleForm.payment_method === 'cash' ? total : (saleForm.payment_method === 'mixed' ? parsePrice(saleForm.cash_amount) : 0),
        card_amount: saleForm.payment_method === 'card' ? total : (saleForm.payment_method === 'mixed' ? parsePrice(saleForm.card_amount) : 0),
        check_amount: saleForm.payment_method === 'check' ? total : (saleForm.payment_method === 'mixed' ? parsePrice(saleForm.check_amount) : 0),
        transfer_amount: saleForm.payment_method === 'transfer' ? total : (saleForm.payment_method === 'mixed' ? parsePrice(saleForm.transfer_amount) : 0),
        check_number: saleForm.check_number,
        cash_given: cashGiven > 0 ? cashGiven : null,
        cash_change: cashChange > 0 ? cashChange : null,
        client_id: saleForm.client_id,
        client_name: saleForm.client_name,
        client_email: saleForm.client_email,
        client_phone: saleForm.client_phone,
        client_phone: saleForm.client_phone,
        vehicle_plate: saleForm.vehicle_plate,
        vehicle_model: saleForm.vehicle_model,
        vehicle_year: saleForm.vehicle_year,
        vehicle_color: saleForm.vehicle_color,
        notes: saleForm.notes,
        subscription_id: selectedSubscription?.id || null
      };

      const res = await axios.post(`${API}/cash-register/sale`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Deduct subscription if selected
      if (selectedSubscription && saleForm.client_id) {
        try {
          await axios.post(
            `${API}/admin/users/${saleForm.client_id}/subscriptions/${selectedSubscription.id}/use`,
            {},
            { headers: { Authorization: `Bearer ${token}` }}
          );
          toast.success(`Abonnement "${selectedSubscription.formula_name}" - 1 utilisation décomptée`);
        } catch (error) {
          console.error('Error deducting subscription:', error);
        }
      }
      
      toast.success(`Vente ${res.data.sale_number} enregistrée`);
      setLastSale(res.data.sale);
      setShowNewSaleModal(false);
      setShowPrintModal(true);
      resetSaleForm();
      fetchCashRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement de la vente');
    }
  };

  const cancelSale = async (saleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette vente ?')) return;
    
    try {
      const res = await axios.post(`${API}/cash-register/sale/${saleId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vente annulée');
      fetchCashRegisterData();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  // Edit/correct a sale
  const [editingSale, setEditingSale] = useState(null);
  
  const startEditSale = (sale) => {
    setEditingSale(sale);
    setSaleForm({
      items: sale.items.map(item => ({
        ...item,
        unit_price: item.unit_price?.toString() || (item.total / item.quantity).toString()
      })),
      register_source: sale.register_source || 'comptoir',
      payment_method: sale.payment_method,
      cash_amount: sale.cash_amount?.toString() || '',
      card_amount: sale.card_amount?.toString() || '',
      check_amount: sale.check_amount?.toString() || '',
      check_number: sale.check_number || '',
      cash_given: sale.cash_given?.toString() || '',
      cash_change: sale.cash_change || 0,
      client_id: sale.client_id || '',
      client_name: sale.client_name || '',
      client_email: sale.client_email || '',
      client_phone: sale.client_phone || '',
      vehicle_plate: sale.vehicle_plate || '',
      vehicle_model: sale.vehicle_model || '',
      vehicle_year: sale.vehicle_year || '',
      vehicle_color: sale.vehicle_color || '',
      notes: sale.notes || ''
    });
    setShowNewSaleModal(true);
  };

  const updateSale = async () => {
    if (!editingSale) return;
    
    const { subtotalHT, taxAmount, total } = calculateSaleTotals();
    
    // Validate required fields
    if (!saleForm.client_name || !saleForm.client_email || !saleForm.client_phone) {
      toast.error('Nom, email et téléphone sont obligatoires');
      return;
    }

    try {
      const updateData = {
        items: saleForm.items.filter(i => i.description && i.total > 0).map(item => ({
          ...item,
          unit_price: parsePrice(item.unit_price)
        })),
        subtotal: subtotalHT,
        tax_amount: taxAmount,
        total: total,
        register_source: saleForm.register_source || 'comptoir',
        payment_method: saleForm.payment_method,
        cash_amount: saleForm.payment_method === 'cash' ? total : parsePrice(saleForm.cash_amount),
        card_amount: saleForm.payment_method === 'card' ? total : parsePrice(saleForm.card_amount),
        check_amount: saleForm.payment_method === 'check' ? total : parsePrice(saleForm.check_amount),
        check_number: saleForm.check_number,
        client_name: saleForm.client_name,
        client_email: saleForm.client_email,
        client_phone: saleForm.client_phone,
        vehicle_plate: saleForm.vehicle_plate,
        vehicle_model: saleForm.vehicle_model,
        vehicle_year: saleForm.vehicle_year,
        vehicle_color: saleForm.vehicle_color,
        notes: saleForm.notes
      };

      await axios.put(`${API}/cash-register/sale/${editingSale.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Vente corrigée');
      setShowNewSaleModal(false);
      setEditingSale(null);
      resetSaleForm();
      fetchCashRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
    }
  };

  const resetSaleForm = () => {
    setSelectedClient(null);
    setClientSearch('');
    setClientSubscriptions([]);
    setSelectedSubscription(null);
    setSaleForm({
      items: [{ description: '', category: 'service', quantity: 1, unit_price: '', total: 0 }],
      register_source: 'comptoir',
      payment_method: 'cash',
      cash_amount: '',
      card_amount: '',
      check_amount: '',
      check_number: '',
      cash_given: '',
      cash_change: 0,
      client_id: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      vehicle_plate: '',
      vehicle_model: '',
      vehicle_year: '',
      vehicle_color: '',
      notes: ''
    });
  };

  // Ajouter un service prédéfini
  const addPredefinedService = (service, category) => {
    if (service.price === 0) {
      toast.info('Ce service nécessite un devis personnalisé');
      return;
    }
    const lastEmptyIndex = saleForm.items.findIndex(i => !i.description);
    if (lastEmptyIndex >= 0) {
      const newItems = [...saleForm.items];
      newItems[lastEmptyIndex] = {
        description: service.description,
        category: category,
        quantity: 1,
        unit_price: service.price.toString(),
        total: service.price
      };
      setSaleForm({ ...saleForm, items: newItems });
    } else {
      setSaleForm({
        ...saleForm,
        items: [...saleForm.items, {
          description: service.description,
          category: category,
          quantity: 1,
          unit_price: service.price.toString(),
          total: service.price
        }]
      });
    }
  };

  // Les fonctions printThermalReceipt, printSaleInvoice sont maintenant importées de '../components/admin/printUtils'
  // printInvoice pour les ventes utilise printSaleInvoice
  const printInvoice = printSaleInvoice;
  
  // Ancien ticket (garde pour compatibilité)
  const printReceipt = (sale) => {
    printThermalReceipt(sale);
  };

  // Envoi de facture par email (via backend)
  const sendInvoiceByEmail = async (sale) => {
    if (!sale.client_email) {
      toast.error('Aucun email client renseigné pour cette vente');
      return;
    }
    
    try {
      await axios.post(`${API}/cash-register/sale/${sale.id}/send-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Facture envoyée à ${sale.client_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la facture');
    }
  };

  const resetUserPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail || !newPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setResetLoading(true);
    try {
      await axios.post(`${API}/admin/reset-user-password`, 
        { email: resetEmail, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Mot de passe réinitialisé pour ${resetEmail}`);
      setResetEmail('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResetLoading(false);
    }
  };

  // Client management functions
  const resetClientForm = () => {
    setClientForm({
      name: '',
      firstname: '',
      company: '',
      email: '',
      phone: '',
      birth_date: '',
      address: '',
      city: '',
      postal_code: '',
      notes: ''
    });
    setEditingClient(null);
  };

  const openEditClient = (client) => {
    setClientForm({
      name: client.name || '',
      firstname: client.firstname || '',
      company: client.company || '',
      email: client.email || '',
      phone: client.phone || '',
      birth_date: client.birth_date || '',
      address: client.address || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      notes: client.notes || ''
    });
    setEditingClient(client);
    setShowClientForm(true);
  };

  const saveClient = async (e) => {
    e.preventDefault();
    
    if (!clientForm.name || !clientForm.email) {
      toast.error('Le nom et l\'email sont obligatoires');
      return;
    }

    try {
      if (editingClient) {
        // Update existing client
        await axios.put(`${API}/admin/users/${editingClient.id}`, clientForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client mis à jour');
      } else {
        // Create new client
        await axios.post(`${API}/admin/users`, clientForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client créé avec succès');
      }
      setShowClientForm(false);
      resetClientForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client supprimé');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Messaging functions
  const openMessageModal = (client) => {
    setMessageRecipient(client);
    setMessageForm({ subject: '', message: '', message_type: 'internal' });
    setShowMessageModal(true);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageRecipient || !messageForm.subject || !messageForm.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/admin/messages/send`, {
        recipient_id: messageRecipient.id,
        subject: messageForm.subject,
        message: messageForm.message,
        message_type: messageForm.message_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'delivered' || response.data.status === 'sent') {
        toast.success(`Message ${messageForm.message_type === 'sms' ? 'SMS' : 'interne'} envoyé à ${messageRecipient.name}`);
      } else if (response.data.status === 'pending_sms') {
        toast.warning('SMS en attente - Twilio non configuré');
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
      
      setShowMessageModal(false);
      setMessageRecipient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  const openWhatsApp = async (client) => {
    if (!client.phone) {
      toast.error('Ce client n\'a pas de numéro de téléphone');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/whatsapp-link/${client.id}`, {
        params: { message: `Bonjour ${client.firstname || client.name}, ` },
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur WhatsApp');
    }
  };

  // Marketing functions
  const fetchMarketingData = async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/auto-messages/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/broadcast/history`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const bdSettings = settingsRes.data.find(s => s.type === 'birthday');
      if (bdSettings) {
        setBirthdaySettings(bdSettings);
      }
      setBroadcastHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching marketing data:', error);
    }
  };

  const saveBirthdaySettings = async () => {
    try {
      await axios.put(`${API}/admin/auto-messages/settings`, {
        type: 'birthday',
        ...birthdaySettings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres d\'anniversaire sauvegardés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const testBirthdayCheck = async () => {
    try {
      await axios.post(`${API}/admin/auto-messages/test-birthday`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vérification des anniversaires effectuée');
    } catch (error) {
      toast.error('Erreur lors du test');
    }
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastForm.subject || !broadcastForm.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSendingBroadcast(true);
    try {
      const response = await axios.post(`${API}/admin/broadcast`, broadcastForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.message}`);
      setBroadcastForm({ subject: '', message: '', message_type: 'internal', filter_type: 'all' });
      fetchMarketingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const search = clientSearch.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(search) ||
      (u.firstname || '').toLowerCase().includes(search) ||
      (u.email || '').toLowerCase().includes(search) ||
      (u.company || '').toLowerCase().includes(search) ||
      (u.phone || '').includes(search)
    );
  });

  const updateAppointmentStatus = async (id, status, price = null) => {
    try {
      let url = `${API}/admin/appointments/${id}?status=${status}`;
      if (price !== null && price > 0) {
        url += `&price=${price}`;
      }
      await axios.put(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(status === 'completed' && price ? 'RDV terminé - Facture générée automatiquement' : 'RDV mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCompleteAppointment = (apt) => {
    setCompletingAppointment(apt);
    setAppointmentPrice('');
  };

  const confirmCompleteAppointment = async () => {
    if (!completingAppointment) return;
    const price = parseFloat(appointmentPrice.replace(',', '.')) || 0;
    await updateAppointmentStatus(completingAppointment.id, 'completed', price);
    setCompletingAppointment(null);
    setAppointmentPrice('');
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Supprimer définitivement ce rendez-vous ?')) return;
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RDV supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Edition libre RDV (admin)
  const openEditApptAdmin = (apt) => {
    setEditingApptAdmin(apt);
    setApptAdminForm({
      date: apt.date || '',
      time_slot: apt.time_slot || '',
      service_name: apt.service_name || '',
      vehicle_brand: apt.vehicle_brand || '',
      vehicle_model: apt.vehicle_model || '',
      vehicle_plate: apt.vehicle_plate || '',
      user_phone: apt.user_phone || '',
      assigned_to: apt.assigned_to || '',
      notes: apt.notes || '',
      status: apt.status || 'pending',
      price: apt.price ?? ''
    });
  };

  const saveApptAdmin = async () => {
    if (!editingApptAdmin) return;
    setApptAdminSaving(true);
    try {
      const payload = { ...apptAdminForm };
      if (payload.price === '') delete payload.price;
      else payload.price = parseFloat(payload.price);
      await axios.patch(`${API}/admin/appointments/${editingApptAdmin.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RDV modifie');
      setEditingApptAdmin(null);
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setApptAdminSaving(false);
    }
  };

  // Edition libre Lavage (admin)
  const openEditWashAdmin = (b) => {
    setEditingWashAdmin(b);
    setWashAdminForm({
      date: b.date || '',
      time_slot: b.time_slot || '',
      wash_type: b.wash_type || 'clean',
      vehicle_type: b.vehicle_type || 'auto',
      vehicle_info: b.vehicle_info || '',
      notes: b.notes || '',
      status: b.status || 'pending',
      price: b.price ?? '',
      payment_method: b.payment_method || 'systempay',
      paid: !!b.paid
    });
  };

  const saveWashAdmin = async () => {
    if (!editingWashAdmin) return;
    setWashAdminSaving(true);
    try {
      const payload = { ...washAdminForm };
      if (payload.price === '') delete payload.price;
      else payload.price = parseFloat(payload.price);
      await axios.put(`${API}/admin/wash/bookings/${editingWashAdmin.id}`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Reservation modifiee');
      setEditingWashAdmin(null);
      fetchAllData();
      fetchWashSlotsOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setWashAdminSaving(false);
    }
  };

  // Notifications admin
  const markBalanceCollected = async (item, paymentMethod) => {
    if (!window.confirm(`Confirmer encaissement de ${item.remaining_to_collect.toFixed(2)}€ (${item.client_name}) en ${paymentMethod} ?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/pending-balances/${item.reference}/mark-collected`,
        { type: item.type, payment_method: paymentMethod, note: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Solde encaisse : ${item.remaining_to_collect.toFixed(2)}€`);
      // Refresh
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/pending-balances`, { headers });
      setPendingBalances(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur encaissement');
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) { /* silent */ }
  };

  const openNotif = async (n) => {
    try {
      const res = await axios.get(`${API}/admin/notifications/${n.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedNotif(res.data);
      if (!n.is_read) {
        await axios.post(`${API}/admin/notifications/${n.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchNotifications();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
      toast.success('Toutes marquees comme lues');
    } catch (e) { toast.error('Erreur'); }
  };

  const deleteNotif = async (id) => {
    try {
      await axios.delete(`${API}/admin/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
      if (selectedNotif?.id === id) setSelectedNotif(null);
    } catch (e) { toast.error('Erreur'); }
  };

  // Wash booking functions
  const updateWashBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/wash/bookings/${id}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Réservation lavage mise à jour');
      fetchAllData();
      fetchWashSlotsOverview();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteWashBooking = async (id) => {
    if (!window.confirm('Supprimer cette réservation lavage ?')) return;
    try {
      await axios.delete(`${API}/admin/wash/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Réservation lavage supprimée');
      fetchAllData();
      fetchWashSlotsOverview();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const fetchWashSlotsOverview = async () => {
    if (!washDateFilter) return;
    try {
      const response = await axios.get(`${API}/admin/wash/slots/${washDateFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWashSlotsOverview(response.data);
    } catch (error) {
      console.error('Error fetching slots overview:', error);
    }
  };

  useEffect(() => {
    if (washDateFilter && token) {
      fetchWashSlotsOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [washDateFilter, token]);

  const blockSlot = async (timeSlot) => {
    try {
      await axios.post(`${API}/admin/wash/slots/block?date=${washDateFilter}&time_slot=${timeSlot}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Créneau ${timeSlot} bloqué`);
      fetchWashSlotsOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const unblockSlot = async (timeSlot) => {
    try {
      await axios.post(`${API}/admin/wash/slots/unblock?date=${washDateFilter}&time_slot=${timeSlot}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Créneau ${timeSlot} débloqué`);
      fetchWashSlotsOverview();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const blockAllDay = async () => {
    if (!window.confirm(`Bloquer tous les créneaux du ${washDateFilter} ?`)) return;
    try {
      await axios.post(`${API}/admin/wash/slots/block-day?date=${washDateFilter}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tous les créneaux bloqués');
      fetchWashSlotsOverview();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const unblockAllDay = async () => {
    try {
      await axios.post(`${API}/admin/wash/slots/unblock-day?date=${washDateFilter}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tous les créneaux débloqués');
      fetchWashSlotsOverview();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredWashBookings = washBookings.filter(b => {
    if (!washDateFilter) return true;
    return b.date === washDateFilter;
  });

  const updateQuoteStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/quotes/${id}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devis mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const markMessageRead = async (id) => {
    try {
      await axios.put(`${API}/admin/messages/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Invoice functions
  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      const qty = typeof newItems[index].quantity === 'string' 
        ? parseInt(newItems[index].quantity) || 0 
        : newItems[index].quantity;
      const price = typeof newItems[index].unit_price === 'string'
        ? parseFloat(newItems[index].unit_price.replace(',', '.')) || 0
        : newItems[index].unit_price;
      newItems[index].total = qty * price;
    }
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const removeInvoiceItem = (index) => {
    if (invoiceForm.items.length > 1) {
      const newItems = invoiceForm.items.filter((_, i) => i !== index);
      setInvoiceForm({ ...invoiceForm, items: newItems });
    }
  };

  const calculateInvoiceTotals = () => {
    // unit_price est le prix HT saisi par l'utilisateur
    const subtotalHT = invoiceForm.items.reduce((sum, item) => {
      const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
      const priceHT = typeof item.unit_price === 'string' 
        ? parseFloat(item.unit_price.replace(',', '.')) || 0 
        : item.unit_price;
      return sum + (qty * priceHT);
    }, 0);
    const taxAmount = subtotalHT * 0.2;
    const total = subtotalHT + taxAmount; // Total TTC
    return { subtotal: subtotalHT, taxAmount, total };
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    const { subtotal, taxAmount, total } = calculateInvoiceTotals();
    
    if (!invoiceForm.user_name || !invoiceForm.user_email || invoiceForm.items.length === 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const invoiceData = {
        ...invoiceForm,
        items: invoiceForm.items.map(item => {
          const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
          const price = typeof item.unit_price === 'string' 
            ? parseFloat(item.unit_price.replace(',', '.')) || 0 
            : item.unit_price;
          return {
            description: item.description,
            quantity: qty,
            unit_price: price,
            total: qty * price
          };
        }),
        subtotal,
        tax_rate: 20,
        tax_amount: taxAmount,
        total
      };

      await axios.post(`${API}/admin/invoices`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Facture créée avec succès');
      setShowInvoiceForm(false);
      setInvoiceForm({
        user_name: '',
        user_firstname: '',
        user_company: '',
        user_email: '',
        user_phone: '',
        user_address: '',
        items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
        notes: '',
        due_date: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la création de la facture');
    }
  };

  const updateInvoiceStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/invoices/${id}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Facture mise à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await axios.delete(`${API}/admin/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Facture supprimée');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Convert invoice back to quote
  const convertInvoiceToQuote = async (invoice) => {
    if (!window.confirm('Convertir cette facture en devis ?\n\nLa facture sera supprimée et un nouveau devis sera créé avec les mêmes informations.')) return;
    
    try {
      // Calculate totals from items (items are in HT)
      const totalHT = invoice.items.reduce((sum, item) => sum + (item.total || item.quantity * item.unit_price), 0);
      const taxAmount = totalHT * 0.2;
      const totalTTC = totalHT + taxAmount;

      // Create a new quote from invoice data
      const quoteData = {
        name: invoice.user_name || '',
        firstname: invoice.user_firstname || '',
        company: invoice.user_company || '',
        email: invoice.user_email || '',
        phone: invoice.user_phone || '',
        address: invoice.user_address || '',
        vehicle_plate: invoice.vehicle_plate || '',
        vehicle_model: invoice.vehicle_model || '',
        items: invoice.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total || item.quantity * item.unit_price
        })),
        notes: invoice.notes || `Converti depuis facture ${invoice.invoice_number}`,
        validity_days: 30,
        subtotal: totalHT,
        tax_rate: 20,
        tax_amount: taxAmount,
        total: totalTTC
      };
      
      // Create the quote
      await axios.post(`${API}/admin/professional-quotes`, quoteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Delete the invoice
      await axios.delete(`${API}/admin/invoices/${invoice.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Facture convertie en devis');
      fetchAllData();
    } catch (error) {
      console.error('Error converting invoice to quote:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    }
  };

  // Edit invoice
  const startEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceForm({
      user_name: invoice.user_name || '',
      user_firstname: invoice.user_firstname || '',
      user_company: invoice.user_company || '',
      user_email: invoice.user_email || '',
      user_phone: invoice.user_phone || '',
      user_address: invoice.user_address || '',
      items: invoice.items || [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
      notes: invoice.notes || '',
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : ''
    });
    setShowInvoiceForm(true);
  };

  const updateInvoice = async (e) => {
    e.preventDefault();
    if (!editingInvoice) return;
    
    const subtotal = invoiceForm.items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxRate = 20;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    const invoiceData = {
      ...invoiceForm,
      subtotal,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total
    };

    try {
      await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Facture modifiée');
      setShowInvoiceForm(false);
      setEditingInvoice(null);
      resetInvoiceForm();
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      user_name: '',
      user_firstname: '',
      user_company: '',
      user_email: '',
      user_phone: '',
      user_address: '',
      items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
      notes: '',
      due_date: ''
    });
    setEditingInvoice(null);
  };

  // Cancel invoice
  const cancelInvoice = async (invoice) => {
    if (!window.confirm(`Annuler la facture ${invoice.invoice_number} ?`)) return;
    try {
      await axios.put(`${API}/admin/invoices/${invoice.id}?status=cancelled`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Facture annulée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  // Create credit note (avoir)
  const openCreditNoteForm = (invoice) => {
    setCreditNoteInvoice(invoice);
    setShowCreditNoteForm(true);
  };

  // Reset collaborator password
  const resetCollaboratorPassword = async (collaboratorId, name) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${name} ?\n\nUn nouveau mot de passe sera généré automatiquement.`)) return;
    
    try {
      const response = await axios.post(`${API}/admin/collaborators/${collaboratorId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show the new password in an alert so admin can share it
      alert(`✅ Mot de passe réinitialisé !\n\n📧 Email: ${response.data.email}\n🔐 Nouveau mot de passe: ${response.data.new_password}\n\n⚠️ Notez ce mot de passe et communiquez-le au collaborateur.`);
      toast.success(`Mot de passe de ${name} réinitialisé`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const createCreditNote = async (e) => {
    e.preventDefault();
    if (!creditNoteInvoice) return;

    const creditNoteData = {
      original_invoice_id: creditNoteInvoice.id,
      original_invoice_number: creditNoteInvoice.invoice_number,
      user_name: creditNoteInvoice.user_name,
      user_firstname: creditNoteInvoice.user_firstname,
      user_company: creditNoteInvoice.user_company,
      user_email: creditNoteInvoice.user_email,
      user_phone: creditNoteInvoice.user_phone,
      user_address: creditNoteInvoice.user_address,
      items: creditNoteInvoice.items.map(item => ({
        ...item,
        total: -Math.abs(item.total),
        unit_price: -Math.abs(item.unit_price)
      })),
      subtotal: -Math.abs(creditNoteInvoice.subtotal),
      tax_rate: creditNoteInvoice.tax_rate,
      tax_amount: -Math.abs(creditNoteInvoice.tax_amount),
      total: -Math.abs(creditNoteInvoice.total),
      type: 'credit_note',
      notes: `Avoir sur facture ${creditNoteInvoice.invoice_number}`
    };

    try {
      await axios.post(`${API}/admin/invoices`, creditNoteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Avoir créé avec succès');
      setShowCreditNoteForm(false);
      setCreditNoteInvoice(null);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la création de l\'avoir');
    }
  };

  // Quote management functions
  const resetQuoteForm = () => {
    setQuoteForm({
      client_name: '',
      client_firstname: '',
      client_company: '',
      client_email: '',
      client_phone: '',
      client_address: '',
      vehicle_plate: '',
      vehicle_model: '',
      items: [{ description: '', quantity: 1, unit_price: '', total: 0 }],
      notes: '',
      validity_days: 30,
      from_quote_request_id: null
    });
    setEditingQuote(null);
  };

  const startEditQuote = (quote) => {
    setEditingQuote(quote);
    setQuoteForm({
      client_name: quote.name || '',
      client_firstname: quote.firstname || '',
      client_company: quote.company || '',
      client_email: quote.email || '',
      client_phone: quote.phone || '',
      client_address: quote.address || '',
      vehicle_plate: quote.vehicle_plate || '',
      vehicle_model: quote.vehicle_model || '',
      items: quote.items || [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
      notes: quote.notes || '',
      validity_days: quote.validity_days || 30
    });
    setShowQuoteForm(true);
  };

  const createOrUpdateQuote = async (e) => {
    e.preventDefault();
    // Les prix saisis sont HT
    const totalHT = quoteForm.items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxRate = 20;
    const taxAmount = totalHT * 0.2;
    const totalTTC = totalHT + taxAmount;

    const quoteData = {
      name: quoteForm.client_name,
      firstname: quoteForm.client_firstname,
      company: quoteForm.client_company,
      email: quoteForm.client_email,
      phone: quoteForm.client_phone,
      address: quoteForm.client_address,
      vehicle_plate: quoteForm.vehicle_plate,
      vehicle_model: quoteForm.vehicle_model,
      items: quoteForm.items,
      notes: quoteForm.notes,
      validity_days: parseInt(quoteForm.validity_days) || 30,
      subtotal: totalHT,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total: totalTTC,
      from_quote_request_id: quoteForm.from_quote_request_id || null
    };

    try {
      if (editingQuote) {
        await axios.put(`${API}/admin/professional-quotes/${editingQuote.id}`, quoteData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Devis modifié');
      } else {
        await axios.post(`${API}/admin/professional-quotes`, quoteData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Devis créé');
      }
      setShowQuoteForm(false);
      resetQuoteForm();
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde du devis');
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await axios.delete(`${API}/admin/professional-quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devis supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteQuoteRequest = async (id) => {
    if (!window.confirm('Supprimer cette demande de devis ?')) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const convertQuoteToInvoice = async (quote) => {
    if (!window.confirm('Convertir ce devis en facture ?')) return;
    
    try {
      await axios.post(`${API}/admin/professional-quotes/${quote.id}/convert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devis converti en facture');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    }
  };

  const updateQuoteProfessionalStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/professional-quotes/${id}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Statut mis à jour: ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Send quote to client with email notification
  const sendQuoteToClient = async (quote) => {
    if (!quote.email) {
      toast.error('Ce devis n\'a pas d\'adresse email client');
      return;
    }
    if (!window.confirm(`Envoyer ce devis à ${quote.email} ?\n\nLe client recevra une notification par email.`)) return;
    
    try {
      const res = await axios.post(`${API}/admin/professional-quotes/${quote.id}/send-to-client`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sent = res?.data?.email_sent;
      const pdf = res?.data?.pdf_attached;
      if (sent) {
        toast.success(`Devis envoyé à ${quote.email}${pdf ? ' avec PDF joint' : ''}`);
      } else {
        toast.warning(`Devis marqué comme envoyé. Email NON délivré (SMTP indisponible). Le client le verra dans son espace.`);
      }
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  // Send invoice (PDF) by email to client
  const sendInvoiceToClient = async (invoice) => {
    if (!invoice.user_email) {
      toast.error('Cette facture n\'a pas d\'adresse email client');
      return;
    }
    if (!window.confirm(`Envoyer cette facture à ${invoice.user_email} ?\n\nLe client recevra un email avec le PDF en pièce jointe.`)) return;
    try {
      const res = await axios.post(`${API}/admin/invoices/${invoice.id}/send-to-client`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sent = res?.data?.email_sent;
      const pdf = res?.data?.pdf_attached;
      if (sent) {
        toast.success(`Facture envoyée à ${invoice.user_email}${pdf ? ' avec PDF joint' : ''}`);
      } else {
        toast.warning('Facture marquée comme envoyée. Email NON délivré (SMTP indisponible). Le client la verra dans son espace.');
      }
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  // Create quote from a client quote request
  const createQuoteFromRequest = (request) => {
    resetQuoteForm();
    setQuoteForm({
      client_name: request.name || '',
      client_firstname: '',
      client_company: '',
      client_email: request.email || '',
      client_phone: request.phone || '',
      client_address: '',
      vehicle_plate: request.vehicle_plate || '',
      vehicle_model: `${request.vehicle_brand || ''} ${request.vehicle_model || ''}`.trim(),
      items: [{ description: request.service_type || request.description || '', quantity: 1, unit_price: '', total: 0 }],
      notes: request.description || '',
      validity_days: 30,
      from_quote_request_id: request.id
    });
    setShowQuoteForm(true);
  };

  // printClientRequestSheet is now imported from '../components/admin/printUtils'

  // ============ CA REPORTS FUNCTIONS ============
  const fetchCAReport = async (type = reportType) => {
    setLoadingReport(true);
    try {
      let url = '';
      if (type === 'daily') {
        url = `${API}/cash-register/report/daily?date=${reportDate}`;
      } else if (type === 'monthly') {
        url = `${API}/cash-register/report/monthly?month=${reportMonth}`;
      } else if (type === 'yearly') {
        url = `${API}/cash-register/report/yearly?year=${reportYear}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(res.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoadingReport(false);
    }
  };

  const exportReportCSV = () => {
    if (!reportData) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    
    if (reportType === 'daily') {
      csvContent += "Rapport Journalier - " + reportDate + "\n\n";
      csvContent += "Métrique,Valeur\n";
      csvContent += `Total CA,${reportData.total_sales?.toFixed(2)} €\n`;
      csvContent += `Nombre de ventes,${reportData.sales_count}\n`;
      csvContent += `Total Espèces,${reportData.total_cash?.toFixed(2)} €\n`;
      csvContent += `Total CB,${reportData.total_card?.toFixed(2)} €\n`;
      csvContent += `Total Chèques,${reportData.total_check?.toFixed(2)} €\n`;
      csvContent += `Annulations,${reportData.cancelled_count} (${reportData.cancelled_total?.toFixed(2)} €)\n`;
      
      if (reportData.sales?.length > 0) {
        csvContent += "\n\nDétail des ventes\n";
        csvContent += "N°,Client,Montant,Mode paiement,Heure\n";
        reportData.sales.forEach(sale => {
          csvContent += `${sale.sale_number},${sale.client_name || 'N/A'},${sale.total?.toFixed(2)} €,${sale.payment_method},${new Date(sale.created_at).toLocaleTimeString('fr-FR')}\n`;
        });
      }
    } else if (reportType === 'monthly') {
      csvContent += "Rapport Mensuel - " + reportMonth + "\n\n";
      csvContent += "Métrique,Valeur\n";
      csvContent += `Total CA,${reportData.total_sales?.toFixed(2)} €\n`;
      csvContent += `Nombre de ventes,${reportData.sales_count}\n`;
      csvContent += `Panier moyen,${reportData.average_sale?.toFixed(2)} €\n`;
      csvContent += `Total Espèces,${reportData.total_cash?.toFixed(2)} €\n`;
      csvContent += `Total CB,${reportData.total_card?.toFixed(2)} €\n`;
      csvContent += `Total Chèques,${reportData.total_check?.toFixed(2)} €\n`;
      
      if (reportData.daily_breakdown?.length > 0) {
        csvContent += "\n\nCA par jour\n";
        csvContent += "Date,Nombre ventes,Total\n";
        reportData.daily_breakdown.forEach(day => {
          csvContent += `${day.date},${day.count},${day.total?.toFixed(2)} €\n`;
        });
      }
    } else if (reportType === 'yearly') {
      csvContent += "Rapport Annuel - " + reportYear + "\n\n";
      csvContent += "Métrique,Valeur\n";
      csvContent += `Total Caisse,${reportData.total_sales?.toFixed(2)} €\n`;
      csvContent += `Total Factures,${reportData.total_invoices?.toFixed(2)} €\n`;
      csvContent += `CA Total,${reportData.grand_total?.toFixed(2)} €\n`;
      csvContent += `Nombre de ventes caisse,${reportData.sales_count}\n`;
      csvContent += `Nombre de factures,${reportData.invoices_count}\n`;
      csvContent += `Panier moyen,${reportData.average_sale?.toFixed(2)} €\n`;
      
      if (reportData.monthly_breakdown?.length > 0) {
        csvContent += "\n\nCA par mois\n";
        csvContent += "Mois,Ventes,Espèces,CB,Chèques,Total\n";
        reportData.monthly_breakdown.forEach(m => {
          const monthNum = parseInt(m.month.split('-')[1]) - 1;
          csvContent += `${monthNames[monthNum]},${m.count},${m.cash?.toFixed(2)} €,${m.card?.toFixed(2)} €,${m.check?.toFixed(2)} €,${m.total?.toFixed(2)} €\n`;
        });
      }
    }
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `rapport_ca_${reportType}_${reportType === 'daily' ? reportDate : reportType === 'monthly' ? reportMonth : reportYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Export CSV téléchargé');
  };

  const printCAReport = () => {
    if (!reportData) return;
    
    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    let title = '';
    let periodLabel = '';
    let breakdownRows = '';
    
    if (reportType === 'daily') {
      title = 'Rapport Journalier';
      periodLabel = new Date(reportDate).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      if (reportData.sales?.length > 0) {
        breakdownRows = reportData.sales.map(sale => `
          <tr>
            <td>${sale.sale_number}</td>
            <td>${sale.client_name || 'N/A'}</td>
            <td>${sale.payment_method === 'cash' ? 'Espèces' : sale.payment_method === 'card' ? 'CB' : sale.payment_method === 'transfer' ? 'Virement' : 'Mixte'}</td>
            <td class="amount">${sale.total?.toFixed(2)} €</td>
          </tr>
        `).join('');
      }
    } else if (reportType === 'monthly') {
      title = 'Rapport Mensuel';
      const [year, month] = reportMonth.split('-');
      periodLabel = `${monthNames[parseInt(month) - 1]} ${year}`;
      if (reportData.daily_breakdown?.length > 0) {
        breakdownRows = reportData.daily_breakdown.map(day => `
          <tr>
            <td>${new Date(day.date).toLocaleDateString('fr-FR')}</td>
            <td>${day.count}</td>
            <td></td>
            <td class="amount">${day.total?.toFixed(2)} €</td>
          </tr>
        `).join('');
      }
    } else if (reportType === 'yearly') {
      title = 'Rapport Annuel';
      periodLabel = reportYear;
      if (reportData.monthly_breakdown?.length > 0) {
        breakdownRows = reportData.monthly_breakdown.map(m => {
          const monthNum = parseInt(m.month.split('-')[1]) - 1;
          return `
            <tr>
              <td>${monthNames[monthNum]}</td>
              <td>${m.count}</td>
              <td>Esp: ${m.cash?.toFixed(0)}€ / CB: ${m.card?.toFixed(0)}€</td>
              <td class="amount">${m.total?.toFixed(2)} €</td>
            </tr>
          `;
        }).join('');
      }
    }

    const reportHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title} - ${periodLabel}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 30px; color: #333; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
          .company-info img { max-width: 120px; margin-bottom: 10px; }
          .company-info p { font-size: 11px; color: #666; line-height: 1.4; }
          .report-title { text-align: right; }
          .report-title h1 { color: #dc2626; font-size: 24px; margin-bottom: 5px; }
          .report-title p { font-size: 14px; color: #666; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
          .summary-card { background: #f8f8f8; padding: 15px; border-radius: 8px; text-align: center; border-left: 4px solid #dc2626; }
          .summary-card h3 { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 5px; }
          .summary-card .value { font-size: 24px; font-weight: bold; color: #1a1a1a; }
          .summary-card.highlight { background: #dc2626; border-left-color: #991b1b; }
          .summary-card.highlight h3, .summary-card.highlight .value { color: white; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #1a1a1a; color: white; padding: 12px; text-align: left; font-size: 11px; text-transform: uppercase; }
          td { padding: 10px 12px; border-bottom: 1px solid #eee; }
          td.amount { text-align: right; font-weight: bold; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; font-size: 10px; color: #666; text-align: center; }
          .payment-breakdown { margin: 20px 0; padding: 15px; background: #f0f9ff; border-radius: 8px; }
          .payment-breakdown h4 { margin-bottom: 10px; color: #0369a1; }
          .payment-row { display: flex; justify-content: space-between; padding: 5px 0; }
          @media print { body { padding: 15px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <img src="https://customer-assets.emergentagent.com/job_5fa86a42-f7fc-4c7b-82aa-e23c2c93b925/artifacts/cpgdckfz_image.png" alt="Logo"/>
            <p><strong>SAS KD AUTO SERVICE</strong><br>142 boulevard Murat, 75016 Paris<br>SIRET: 989 707 260 00018 - TVA: FR01989707260</p>
          </div>
          <div class="report-title">
            <h1>${title}</h1>
            <p>${periodLabel}</p>
            <p style="font-size: 11px; margin-top: 5px;">Édité le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
          </div>
        </div>

        <div class="summary">
          <div class="summary-card highlight">
            <h3>CA Total</h3>
            <div class="value">${(reportType === 'yearly' ? reportData.grand_total : reportData.total_sales)?.toFixed(2)} €</div>
          </div>
          <div class="summary-card">
            <h3>Transactions</h3>
            <div class="value">${reportData.sales_count || 0}</div>
          </div>
          <div class="summary-card">
            <h3>Panier moyen</h3>
            <div class="value">${reportData.average_sale?.toFixed(2) || '0.00'} €</div>
          </div>
          <div class="summary-card">
            <h3>Annulations</h3>
            <div class="value">${reportData.cancelled_count || 0}</div>
          </div>
        </div>

        <div class="payment-breakdown">
          <h4>Répartition par mode de paiement</h4>
          <div class="payment-row"><span>💵 Espèces</span><span>${reportData.total_cash?.toFixed(2) || '0.00'} €</span></div>
          <div class="payment-row"><span>💳 Carte bancaire</span><span>${reportData.total_card?.toFixed(2) || '0.00'} €</span></div>
          <div class="payment-row"><span>📝 Chèques</span><span>${reportData.total_check?.toFixed(2) || '0.00'} €</span></div>
          <div class="payment-row"><span>🔄 Virements</span><span>${reportData.total_transfer?.toFixed(2) || '0.00'} €</span></div>
          ${reportType === 'yearly' ? `<div class="payment-row" style="border-top: 1px solid #ccc; margin-top: 10px; padding-top: 10px;"><span>📄 Factures hors caisse</span><span>${reportData.total_invoices?.toFixed(2) || '0.00'} €</span></div>` : ''}
        </div>

        ${breakdownRows ? `
          <h3 style="margin-top: 30px; margin-bottom: 10px;">${reportType === 'daily' ? 'Détail des ventes' : reportType === 'monthly' ? 'CA par jour' : 'CA par mois'}</h3>
          <table>
            <thead>
              <tr>
                <th>${reportType === 'daily' ? 'N° Vente' : reportType === 'monthly' ? 'Date' : 'Mois'}</th>
                <th>${reportType === 'daily' ? 'Client' : 'Nb ventes'}</th>
                <th>${reportType === 'daily' ? 'Paiement' : reportType === 'yearly' ? 'Détail paiements' : ''}</th>
                <th style="text-align: right;">Montant</th>
              </tr>
            </thead>
            <tbody>
              ${breakdownRows}
            </tbody>
          </table>
        ` : ''}

        <div class="footer">
          <p>SAS KD AUTO SERVICE - 142 boulevard Murat, 75016 Paris - SIRET: 989 707 260 00018</p>
          <p>Ce document est généré automatiquement par le système de caisse Le Hub Atelier Auto Moto</p>
        </div>

        <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
      </body>
      </html>
    `;
    try {
      const blob = new Blob([reportHTML], { type: 'text/html; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { console.error('Print error:', e); }
  };

  // Print professional quote as PDF
  const printQuotePDF = (quote) => {
    const validityDate = quote.validity_date ? new Date(quote.validity_date).toLocaleDateString('fr-FR') : '';
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Devis ${quote.quote_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 40px; color: #1a1a1a; font-size: 12px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .company-info h1 { color: #dc2626; font-size: 20px; margin-bottom: 5px; }
          .company-info p { color: #666; line-height: 1.6; }
          .quote-info { text-align: right; }
          .quote-info h2 { color: #dc2626; font-size: 24px; margin-bottom: 10px; }
          .quote-info .number { font-size: 16px; font-weight: bold; color: #333; }
          .client-box { background: #f8f8f8; padding: 20px; border-radius: 8px; margin-bottom: 30px; border-left: 4px solid #dc2626; }
          .client-box h3 { color: #dc2626; margin-bottom: 10px; font-size: 12px; text-transform: uppercase; }
          .client-box p { line-height: 1.6; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #1a1a1a; color: white; padding: 12px; text-align: left; font-size: 11px; text-transform: uppercase; }
          td { padding: 12px; border-bottom: 1px solid #eee; }
          .qty, .price { text-align: right; }
          .totals { margin-left: auto; width: 300px; }
          .totals table { margin-bottom: 0; }
          .totals td { border: none; padding: 8px 12px; }
          .totals .label { text-align: right; color: #666; }
          .totals .value { text-align: right; font-weight: bold; }
          .totals .grand-total { background: #dc2626; color: white; }
          .totals .grand-total td { font-size: 14px; }
          .validity { background: #fef3c7; padding: 15px; border-radius: 8px; margin-bottom: 20px; text-align: center; }
          .validity strong { color: #d97706; }
          .notes { background: #f0f9ff; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .notes h4 { color: #0369a1; margin-bottom: 8px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; font-size: 10px; color: #666; }
          .signature { margin-top: 40px; display: flex; justify-content: space-between; }
          .signature-box { width: 45%; border: 1px dashed #ccc; padding: 20px; text-align: center; }
          .signature-box p { margin-bottom: 40px; font-size: 11px; color: #666; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <img src="https://customer-assets.emergentagent.com/job_5fa86a42-f7fc-4c7b-82aa-e23c2c93b925/artifacts/cpgdckfz_image.png" style="max-width: 150px; margin-bottom: 10px;" alt="Le Hub Atelier"/>
            <p><strong>SAS KD AUTO SERVICE</strong></p>
            <p>142 boulevard Murat<br>75016 Paris<br>Tél: 0805 032 032<br>Email: info@lehubatelier.fr<br>SIREN: 989 707 260<br>SIRET: 989 707 260 00018<br>N° TVA: FR01989707260</p>
          </div>
          <div class="quote-info">
            <h2>DEVIS</h2>
            <p class="number">${quote.quote_number}</p>
            <p>Date: ${new Date(quote.created_at).toLocaleDateString('fr-FR')}</p>
            <p>Validité: ${quote.validity_days} jours</p>
          </div>
        </div>
        
        <div class="client-box">
          <h3>Client</h3>
          <p>
            ${quote.company ? `<strong>${quote.company}</strong><br>` : ''}
            <strong>${quote.firstname || ''} ${quote.name}</strong><br>
            ${quote.email ? `Email: ${quote.email}<br>` : ''}
            ${quote.phone ? `Tél: ${quote.phone}<br>` : ''}
            ${quote.address ? `${quote.address}<br>` : ''}
            ${quote.vehicle_plate ? `Véhicule: ${quote.vehicle_plate} ${quote.vehicle_model || ''}<br>` : ''}
          </p>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th class="qty">Qté</th>
              <th class="price">P.U. HT</th>
              <th class="price">Total HT</th>
            </tr>
          </thead>
          <tbody>
            ${quote.items.map(item => {
              const unitHT = (item.unit_price || item.total / item.quantity) / 1.2;
              const totalHT = item.total / 1.2;
              return `
                <tr>
                  <td>${item.description}</td>
                  <td class="qty">${item.quantity}</td>
                  <td class="price">${unitHT.toFixed(2)} €</td>
                  <td class="price">${totalHT.toFixed(2)} €</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="totals">
          <table>
            <tr>
              <td class="label">Total HT</td>
              <td class="value">${(quote.total / 1.2).toFixed(2)} €</td>
            </tr>
            <tr>
              <td class="label">TVA 20%</td>
              <td class="value">${(quote.total - quote.total / 1.2).toFixed(2)} €</td>
            </tr>
            <tr class="grand-total">
              <td class="label">TOTAL TTC</td>
              <td class="value">${quote.total.toFixed(2)} €</td>
            </tr>
          </table>
        </div>

        <div class="validity">
          <strong>⚠️ Ce devis est valable jusqu'au ${validityDate}</strong>
        </div>

        ${quote.notes ? `<div class="notes"><h4>Notes / Conditions particulières</h4><p>${quote.notes}</p></div>` : ''}

        <div class="signature">
          <div class="signature-box">
            <p>Signature du client</p>
            <p style="font-size: 10px; margin-bottom: 5px;">Bon pour accord, lu et approuvé</p>
            <p style="margin-bottom: 5px;">Date: _______________</p>
          </div>
          <div class="signature-box">
            <p>Pour Le Hub Atelier Auto Moto</p>
            <p style="font-size: 10px; margin-bottom: 5px;">Cachet et signature</p>
          </div>
        </div>

        <div class="footer">
          <p>SAS KD AUTO SERVICE - 142 boulevard Murat, 75016 Paris</p>
          <p>SIREN: 989 707 260 - SIRET: 989 707 260 00018 - N° TVA: FR01989707260</p>
          <p style="margin-top: 5px;">Conditions de paiement: 30% à la commande, solde à la livraison. Tout retard de paiement entraînera des pénalités de retard.</p>
        </div>

        <script>
          window.onload = function() { 
            setTimeout(function() { window.print(); }, 200);
          }
        </script>
      </body>
      </html>
    `;
    const quoteWindow = window.open('', '_blank');
    if (quoteWindow) {
      quoteWindow.document.write(htmlContent);
      quoteWindow.document.close();
    } else {
      try {
        const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) { console.error('Print error:', e); }
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!user || (user.role !== 'admin' && user.role !== 'collaborator')) return null;

  return (
    <div className="min-h-screen bg-[#09090B] py-8">
      {/* Modal for completing appointment with price */}
      {completingAppointment && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-white">Terminer le RDV</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-zinc-300">
                <p><strong>Client:</strong> {completingAppointment.user_name}</p>
                <p><strong>Service:</strong> {completingAppointment.service_name}</p>
                <p><strong>Véhicule:</strong> {completingAppointment.vehicle_brand} {completingAppointment.vehicle_model}</p>
              </div>
              <div>
                <Label className="text-zinc-300">Montant TTC de la prestation (€)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="Ex: 150.00"
                  value={appointmentPrice}
                  onChange={(e) => setAppointmentPrice(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="appointment-price-input"
                />
                <p className="text-xs text-zinc-500 mt-1">Une facture sera automatiquement générée pour le client</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={confirmCompleteAppointment}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                  data-testid="confirm-complete-btn"
                >
                  <Receipt className="mr-2" size={16} />
                  Terminer et facturer
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setCompletingAppointment(null)}
                  className="border-zinc-700"
                >
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal for opening cash register */}
      {showOpenCashModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Unlock className="text-green-400" size={20} />
                Ouvrir la caisse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-300">Fond de caisse initial (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 100.00"
                  value={initialCash}
                  onChange={(e) => setInitialCash(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="initial-cash-input"
                />
                <p className="text-xs text-zinc-500 mt-1">Montant en espèces dans la caisse à l'ouverture</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={openCashRegister} className="bg-green-600 hover:bg-green-700 flex-1">
                  <Unlock className="mr-2" size={16} />
                  Ouvrir la caisse
                </Button>
                <Button variant="outline" onClick={() => setShowOpenCashModal(false)} className="border-zinc-700">
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal for closing cash register */}
      {showCloseCashModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lock className="text-red-400" size={20} />
                Fermer la caisse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {cashSession && (
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2">
                  <p className="text-zinc-300">
                    <strong>Fond initial:</strong> {cashSession.initial_cash?.toFixed(2)}€
                  </p>
                  <p className="text-zinc-300">
                    <strong>Encaissements espèces:</strong> {cashSession.total_cash?.toFixed(2)}€
                  </p>
                  <p className="text-green-400 font-bold">
                    <strong>Attendu en caisse:</strong> {(cashSession.initial_cash + cashSession.total_cash)?.toFixed(2)}€
                  </p>
                </div>
              )}
              <div>
                <Label className="text-zinc-300">Montant compté en caisse (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 250.00"
                  value={finalCash}
                  onChange={(e) => setFinalCash(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="final-cash-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={closeCashRegister} variant="destructive" className="bg-red-600 hover:bg-red-700 flex-1">
                  <Lock className="mr-2" size={16} />
                  Fermer la caisse
                </Button>
                <Button variant="outline" onClick={() => setShowCloseCashModal(false)} className="border-zinc-700">
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal for new sale */}
      {showNewSaleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-5xl my-8 max-h-[90vh] overflow-y-auto">
            <CardHeader className="sticky top-0 bg-zinc-900 z-10 border-b border-zinc-800">
              <div className="flex justify-between items-center">
                <CardTitle className="text-white flex items-center gap-2">
                  {editingSale ? (
                    <>
                      <Wrench className="text-yellow-400" size={20} />
                      Correction Vente {editingSale.sale_number}
                    </>
                  ) : (
                    <>
                      <Euro className="text-green-400" size={20} />
                      Nouvelle Vente
                    </>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-1">
                  <Button 
                    size="sm" 
                    variant={priceMode === 'TTC' ? 'default' : 'ghost'}
                    onClick={() => setPriceMode('TTC')}
                    className={priceMode === 'TTC' ? 'bg-green-600' : 'text-zinc-400'}
                  >
                    TTC
                  </Button>
                  <Button 
                    size="sm" 
                    variant={priceMode === 'HT' ? 'default' : 'ghost'}
                    onClick={() => setPriceMode('HT')}
                    className={priceMode === 'HT' ? 'bg-blue-600' : 'text-zinc-400'}
                  >
                    HT
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              {/* Client Search */}
              <div className="bg-zinc-800/30 p-4 rounded-lg">
                <Label className="text-zinc-300 text-sm mb-2 block">🔍 Rechercher un client existant</Label>
                {selectedClient ? (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <div>
                      <p className="text-white font-medium">{selectedClient.name} {selectedClient.firstname || ''}</p>
                      <p className="text-zinc-400 text-sm">{selectedClient.email} {selectedClient.phone ? `• ${selectedClient.phone}` : ''}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={clearSelectedClient} className="text-red-400">
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      placeholder="Tapez le nom, email ou téléphone du client..."
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        searchClients(e.target.value);
                      }}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                    {clientSuggestions.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {clientSuggestions.map((client) => (
                          <div
                            key={client.id}
                            onClick={() => selectClient(client)}
                            className="p-3 hover:bg-zinc-700 cursor-pointer border-b border-zinc-700 last:border-b-0"
                          >
                            <p className="text-white font-medium">{client.name} {client.firstname || ''}</p>
                            <p className="text-zinc-400 text-sm">{client.email}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Client Subscriptions */}
              {selectedClient && clientSubscriptions.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <Label className="text-yellow-400 text-sm mb-2 block">🎫 Abonnements actifs du client</Label>
                  <div className="space-y-2">
                    {clientSubscriptions.map((sub) => (
                      <div 
                        key={sub.id}
                        onClick={() => selectSubscription(sub)}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          selectedSubscription?.id === sub.id 
                            ? 'bg-green-500/20 border-2 border-green-500' 
                            : 'bg-zinc-800/50 hover:bg-zinc-700/50 border border-zinc-700'
                        }`}
                      >
                        <div>
                          <p className="text-white font-medium">{sub.formula_name}</p>
                          <p className="text-zinc-400 text-sm">
                            {sub.remaining_uses} / {sub.total_uses} utilisations restantes
                          </p>
                        </div>
                        <div className="text-right">
                          {selectedSubscription?.id === sub.id ? (
                            <Badge className="bg-green-600">Sélectionné</Badge>
                          ) : (
                            <Badge variant="outline" className="border-yellow-500 text-yellow-400">
                              Cliquez pour utiliser
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedSubscription && (
                    <p className="text-green-400 text-sm mt-2">
                      ✓ Une utilisation sera décomptée de "{selectedSubscription.formula_name}" après validation
                    </p>
                  )}
                </div>
              )}

              {/* Vehicle Info */}
              <div className="bg-zinc-800/30 p-4 rounded-lg">
                <Label className="text-zinc-300 text-sm mb-2 block">🚗 Véhicule (optionnel)</Label>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Immatriculation</Label>
                    <Input
                      placeholder="AA-123-BB"
                      value={saleForm.vehicle_plate}
                      onChange={(e) => setSaleForm({ ...saleForm, vehicle_plate: e.target.value.toUpperCase() })}
                      className="bg-zinc-800 border-zinc-700 text-white uppercase"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Modèle</Label>
                    <Input
                      placeholder="Ex: Peugeot 308"
                      value={saleForm.vehicle_model}
                      onChange={(e) => setSaleForm({ ...saleForm, vehicle_model: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Année</Label>
                    <Input
                      placeholder="2022"
                      value={saleForm.vehicle_year}
                      onChange={(e) => setSaleForm({ ...saleForm, vehicle_year: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Couleur</Label>
                    <Input
                      placeholder="Noir"
                      value={saleForm.vehicle_color}
                      onChange={(e) => setSaleForm({ ...saleForm, vehicle_color: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Services by Category */}
              <div>
                <Label className="text-zinc-300 text-sm mb-2 block">📋 Services rapides</Label>
                {/* Category Tabs */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {Object.entries(SERVICES_BY_CATEGORY).map(([key, cat]) => (
                    <Button
                      key={key}
                      size="sm"
                      variant={activeServiceCategory === key ? 'default' : 'outline'}
                      onClick={() => setActiveServiceCategory(key)}
                      className={`text-xs ${activeServiceCategory === key ? 'bg-red-600' : 'border-zinc-600'}`}
                    >
                      {cat.label}
                    </Button>
                  ))}
                </div>
                {/* Services Grid */}
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-40 overflow-y-auto p-2 bg-zinc-800/30 rounded-lg">
                  {SERVICES_BY_CATEGORY[activeServiceCategory]?.services.map((service, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs border-zinc-600 hover:bg-zinc-700 justify-between"
                      onClick={() => addPredefinedService(service, activeServiceCategory)}
                    >
                      <span className="truncate">{service.description}</span>
                      <span className={service.price > 0 ? 'text-green-400 ml-1' : 'text-yellow-400 ml-1'}>
                        {service.label || `${service.price}€`}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-zinc-300 text-lg">Articles</Label>
                  <Button type="button" onClick={addSaleItem} variant="outline" size="sm" className="border-zinc-700">
                    <Plus size={14} className="mr-1" /> Ajouter ligne
                  </Button>
                </div>
                <div className="space-y-2">
                  {saleForm.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end bg-zinc-800/30 p-2 rounded-lg">
                      <div className="col-span-5">
                        <Input
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateSaleItem(index, 'description', e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Select value={item.category} onValueChange={(v) => updateSaleItem(index, 'category', v)}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="wash">Lavage</SelectItem>
                            <SelectItem value="service">Service</SelectItem>
                            <SelectItem value="product">Produit</SelectItem>
                            <SelectItem value="depannage">Dépannage</SelectItem>
                            <SelectItem value="other">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateSaleItem(index, 'quantity', e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white text-sm text-center"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="Prix"
                          value={item.unit_price}
                          onChange={(e) => updateSaleItem(index, 'unit_price', e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type="text"
                          value={(item.total || 0).toFixed(2) + '€'}
                          readOnly
                          className="bg-zinc-700 border-zinc-600 text-green-400 font-bold text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSaleItem(index)}
                          className="text-red-400 hover:text-red-300 w-full"
                          disabled={saleForm.items.length === 1}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Sous-total HT</span>
                  <span className="text-white">{calculateSaleTotals().subtotalHT.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">TVA 20%</span>
                  <span className="text-white">{calculateSaleTotals().taxAmount.toFixed(2)}€</span>
                </div>
                <div className="border-t border-zinc-700 pt-2 mt-2">
                  <div className="flex justify-between text-xl font-bold">
                    <span className="text-white">TOTAL TTC</span>
                    <span className="text-green-400">{calculateSaleTotals().total.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              {/* Type de caisse : Comptoir (compte dans la caisse) ou Internet (hors caisse, modifiable illimite) */}
              <div className="bg-zinc-800/40 border border-zinc-700 rounded-lg p-4">
                <Label className="text-zinc-300 text-lg mb-3 block">Type de caisse</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={saleForm.register_source === 'comptoir' ? 'default' : 'outline'}
                    className={saleForm.register_source === 'comptoir' ? 'bg-red-600 hover:bg-red-700' : 'border-zinc-700'}
                    onClick={() => setSaleForm({ ...saleForm, register_source: 'comptoir' })}
                    data-testid="sale-source-comptoir"
                  >
                    <Banknote size={16} className="mr-2" />
                    Caisse Comptoir
                  </Button>
                  <Button
                    type="button"
                    variant={saleForm.register_source === 'internet' ? 'default' : 'outline'}
                    className={saleForm.register_source === 'internet' ? 'bg-blue-600 hover:bg-blue-700' : 'border-zinc-700'}
                    onClick={() => setSaleForm({ ...saleForm, register_source: 'internet' })}
                    data-testid="sale-source-internet"
                  >
                    <CreditCard size={16} className="mr-2" />
                    Caisse Internet
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  {saleForm.register_source === 'internet'
                    ? '⚠ Vente Internet : non comptée dans le total caisse, modifiable sans limite.'
                    : '✓ Vente Comptoir : compte dans le total caisse (Z de caisse).'}
                </p>
              </div>

              {/* Payment Method */}
              <div>
                <Label className="text-zinc-300 text-lg mb-2 block">Mode de paiement</Label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'cash', label: 'Espèces', icon: Banknote, color: 'yellow' },
                    { value: 'card', label: 'Carte', icon: CreditCard, color: 'blue' },
                    { value: 'transfer', label: 'Virement', icon: ArrowRightLeft, color: 'green' },
                    { value: 'mixed', label: 'Mixte', icon: Calculator, color: 'cyan' },
                  ].map((method) => (
                    <Button
                      key={method.value}
                      type="button"
                      variant={saleForm.payment_method === method.value ? 'default' : 'outline'}
                      className={saleForm.payment_method === method.value ? `bg-${method.color}-600` : 'border-zinc-700'}
                      onClick={() => setSaleForm({ ...saleForm, payment_method: method.value })}
                    >
                      <method.icon size={16} className="mr-2" />
                      {method.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Mixed payment amounts */}
              {saleForm.payment_method === 'mixed' && (
                <div className="grid grid-cols-3 gap-4 bg-zinc-800/30 p-4 rounded-lg">
                  <div>
                    <Label className="text-zinc-400">Espèces (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={saleForm.cash_amount}
                      onChange={(e) => setSaleForm({ ...saleForm, cash_amount: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Carte (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={saleForm.card_amount}
                      onChange={(e) => setSaleForm({ ...saleForm, card_amount: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Virement (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={saleForm.transfer_amount}
                      onChange={(e) => setSaleForm({ ...saleForm, transfer_amount: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Cash change calculator - for cash payment */}
              {saleForm.payment_method === 'cash' && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <Label className="text-yellow-400 text-lg mb-3 block">💵 Calcul du rendu de monnaie</Label>
                  <div className="grid grid-cols-3 gap-4 items-end">
                    <div>
                      <Label className="text-zinc-300">Total à payer</Label>
                      <Input
                        type="text"
                        value={calculateSaleTotals().total.toFixed(2) + '€'}
                        readOnly
                        className="bg-zinc-700 border-zinc-600 text-green-400 font-bold text-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Montant donné (€)</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0,00"
                        value={saleForm.cash_given}
                        onChange={(e) => {
                          const given = parsePrice(e.target.value);
                          const total = calculateSaleTotals().total;
                          setSaleForm({ 
                            ...saleForm, 
                            cash_given: e.target.value,
                            cash_change: given >= total ? given - total : 0
                          });
                        }}
                        className="bg-zinc-800 border-zinc-700 text-white text-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">À rendre</Label>
                      <Input
                        type="text"
                        value={saleForm.cash_change > 0 ? saleForm.cash_change.toFixed(2) + '€' : '0,00€'}
                        readOnly
                        className={`border-zinc-600 text-lg font-bold ${saleForm.cash_change > 0 ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Client info - REQUIRED */}
              <div className="bg-zinc-800/30 p-4 rounded-lg">
                <Label className="text-zinc-300 text-lg mb-3 block">👤 Informations client <span className="text-red-400">*</span></Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400">Nom & Prénom <span className="text-red-400">*</span></Label>
                    <Input
                      type="text"
                      value={saleForm.client_name}
                      onChange={(e) => setSaleForm({ ...saleForm, client_name: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="Nom du client"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Email <span className="text-red-400">*</span></Label>
                    <Input
                      type="email"
                      value={saleForm.client_email}
                      onChange={(e) => setSaleForm({ ...saleForm, client_email: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="email@exemple.com"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Téléphone <span className="text-red-400">*</span></Label>
                    <Input
                      type="tel"
                      value={saleForm.client_phone}
                      onChange={(e) => setSaleForm({ ...saleForm, client_phone: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      placeholder="06 12 34 56 78"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-zinc-300">Notes</Label>
                <Textarea
                  value={saleForm.notes}
                  onChange={(e) => setSaleForm({ ...saleForm, notes: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="Notes sur la vente..."
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={editingSale ? updateSale : createSale} 
                  className={`flex-1 py-6 text-lg ${editingSale ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  <Check className="mr-2" size={20} />
                  {editingSale ? 'Enregistrer la correction' : `Valider la vente (${calculateSaleTotals().total.toFixed(2)}€)`}
                </Button>
                <Button variant="outline" onClick={() => { setShowNewSaleModal(false); setEditingSale(null); resetSaleForm(); }} className="border-zinc-700">
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Print Modal after sale validation */}
      {showPrintModal && lastSale && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Check className="text-green-400" size={24} />
                Vente enregistrée !
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <p className="text-green-400 font-bold text-xl">{lastSale.sale_number}</p>
                <p className="text-white text-2xl font-bold mt-2">{lastSale.total?.toFixed(2)}€</p>
                {lastSale.client_name && (
                  <p className="text-zinc-400 mt-1">Client: {lastSale.client_name}</p>
                )}
                {lastSale.vehicle_plate && (
                  <p className="text-zinc-400">Véhicule: {lastSale.vehicle_plate}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <p className="text-zinc-300 text-center font-medium">Que souhaitez-vous imprimer ?</p>
                
                <Button 
                  onClick={() => { printThermalReceipt(lastSale); }}
                  className="w-full bg-yellow-600 hover:bg-yellow-700"
                >
                  <Printer className="mr-2" size={18} />
                  Ticket de caisse (Imprimante thermique)
                </Button>
                
                <Button 
                  onClick={() => { printDuplicataReceipt(lastSale); }}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  <Copy className="mr-2" size={18} />
                  Duplicata ticket de caisse
                </Button>
                
                <Button 
                  onClick={() => { printInvoice(lastSale); }}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <FileText className="mr-2" size={18} />
                  Facture PDF (A4)
                </Button>
                
                {lastSale.client_email && (
                  <Button 
                    onClick={() => { sendInvoiceByEmail(lastSale); toast.success('Facture envoyée !'); }}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Mail className="mr-2" size={18} />
                    Envoyer facture par email
                  </Button>
                )}
                
                <Button 
                  onClick={() => { printThermalReceipt(lastSale); printInvoice(lastSale); }}
                  variant="outline"
                  className="w-full border-zinc-600"
                >
                  <Printer className="mr-2" size={18} />
                  Imprimer les deux
                </Button>
              </div>
              
              <Button 
                onClick={() => { setShowPrintModal(false); setLastSale(null); }}
                variant="ghost"
                className="w-full text-zinc-400 hover:text-white"
              >
                Fermer sans imprimer
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal for sending message to client */}
      {showMessageModal && messageRecipient && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageCircle className="text-blue-400" size={20} />
                Envoyer un message
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-300">
                  <strong>Destinataire:</strong> {messageRecipient.firstname} {messageRecipient.name}
                </p>
                <p className="text-zinc-500 text-sm">{messageRecipient.email}</p>
                {messageRecipient.phone && (
                  <p className="text-zinc-500 text-sm">{messageRecipient.phone}</p>
                )}
              </div>
              
              <form onSubmit={sendMessage} className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Type de message</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant={messageForm.message_type === 'internal' ? 'default' : 'outline'}
                      className={messageForm.message_type === 'internal' ? 'bg-blue-600' : 'border-zinc-700'}
                      onClick={() => setMessageForm({...messageForm, message_type: 'internal'})}
                    >
                      <Mail size={14} className="mr-1" /> Message interne
                    </Button>
                    <Button
                      type="button"
                      variant={messageForm.message_type === 'sms' ? 'default' : 'outline'}
                      className={messageForm.message_type === 'sms' ? 'bg-green-600' : 'border-zinc-700'}
                      onClick={() => setMessageForm({...messageForm, message_type: 'sms'})}
                      disabled={!messageRecipient.phone}
                    >
                      <Phone size={14} className="mr-1" /> SMS
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    {messageForm.message_type === 'internal' 
                      ? 'Le client verra ce message dans son espace personnel'
                      : 'Un SMS sera envoyé au numéro du client'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-zinc-300">Objet</Label>
                  <Input
                    value={messageForm.subject}
                    onChange={(e) => setMessageForm({...messageForm, subject: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="Ex: Rappel de rendez-vous"
                    required
                    data-testid="message-subject"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-300">Message</Label>
                  <Textarea
                    value={messageForm.message}
                    onChange={(e) => setMessageForm({...messageForm, message: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="Votre message..."
                    rows={4}
                    required
                    data-testid="message-content"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="submit" 
                    className="bg-blue-600 hover:bg-blue-700 flex-1"
                    disabled={sendingMessage}
                  >
                    <Send size={16} className="mr-2" />
                    {sendingMessage ? 'Envoi...' : 'Envoyer'}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => { setShowMessageModal(false); setMessageRecipient(null); }}
                    className="border-zinc-700"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            TABLEAU DE <span className="text-red-500">BORD</span>
          </h1>
          <p className="text-zinc-400">Gestion du garage Le Hub Atelier</p>
        </div>

        {/* Stats - Clickable cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
            <Card 
              className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-blue-500 hover:bg-zinc-800/50 transition-all"
              onClick={() => setActiveTab('appointments')}
              data-testid="stat-rdv"
            >
              <CardContent className="p-3 md:p-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <Calendar className="text-blue-400 shrink-0" size={20} />
                  <div className="min-w-0">
                    <p className="text-xl md:text-2xl font-bold text-white">{stats.appointments.total}</p>
                    <p className="text-zinc-500 text-xs md:text-sm">RDV total</p>
                    <Badge className="bg-yellow-500 mt-1 text-xs">{stats.appointments.pending} en attente</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-green-500 hover:bg-zinc-800/50 transition-all"
              onClick={() => setActiveTab('quotes')}
              data-testid="stat-devis"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <FileText className="text-green-400" size={24} />
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.quotes.total}</p>
                    <p className="text-zinc-500 text-sm">Devis</p>
                    <Badge className="bg-yellow-500 mt-1">{stats.quotes.pending} en attente</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-purple-500 hover:bg-zinc-800/50 transition-all"
              onClick={() => setActiveTab('users')}
              data-testid="stat-clients"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="text-purple-400" size={24} />
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.users}</p>
                    <p className="text-zinc-500 text-sm">Clients</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-cyan-500 hover:bg-zinc-800/50 transition-all"
              onClick={() => setActiveTab('users')}
              data-testid="stat-abonnes"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <BarChart3 className="text-cyan-400" size={24} />
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.subscriptions}</p>
                    <p className="text-zinc-500 text-sm">Abonnés actifs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-red-500 hover:bg-zinc-800/50 transition-all"
              onClick={() => setActiveTab('messages')}
              data-testid="stat-messages"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <MessageSquare className="text-red-400" size={24} />
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.messages_unread}</p>
                    <p className="text-zinc-500 text-sm">Messages non lus</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-emerald-500 hover:bg-zinc-800/50 transition-all"
              onClick={() => setActiveTab('invoices')}
              data-testid="stat-factures"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Euro className="text-emerald-400" size={24} />
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.invoices?.total || 0}</p>
                    <p className="text-zinc-500 text-sm">Factures</p>
                    <Badge className="bg-yellow-500 mt-1">{stats.invoices?.unpaid || 0} impayées</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex flex-nowrap md:flex-wrap gap-1 p-1 h-auto w-full md:w-auto overflow-x-auto justify-start scrollbar-thin" data-testid="admin-tabs-list">
            <TabsTrigger value="inbox" className="data-[state=active]:bg-red-600 relative text-xs md:text-sm" data-testid="tab-inbox">
              <Bell className="mr-1 md:mr-2" size={14} />
              <span className="hidden sm:inline">Boite</span>
              <span className="sm:hidden">Box</span>
              {unreadCount > 0 && (
                <span className="ml-1 md:ml-2 bg-red-600 text-white text-xs rounded-full px-2 py-0.5 font-bold" data-testid="unread-badge">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="cash" className="data-[state=active]:bg-green-600 text-xs md:text-sm">
              <Banknote className="mr-1 md:mr-2" size={14} />
              Caisse
            </TabsTrigger>
            <TabsTrigger value="balances" className="data-[state=active]:bg-amber-600 text-xs md:text-sm" data-testid="tab-balances">
              <Euro className="mr-1 md:mr-2" size={14} />
              <span className="hidden sm:inline">Soldes</span>
              <span className="sm:hidden">€</span>
            </TabsTrigger>
            <TabsTrigger value="appointments" className="data-[state=active]:bg-red-600 text-xs md:text-sm">
              <Calendar className="mr-1 md:mr-2" size={14} />
              <span className="hidden sm:inline">Rendez-vous</span>
              <span className="sm:hidden">RDV</span>
            </TabsTrigger>
            <TabsTrigger value="wash" className="data-[state=active]:bg-red-600 text-xs md:text-sm">
              <Droplets className="mr-1 md:mr-2" size={14} />
              Lavage
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-red-600 text-xs md:text-sm">
              <FileText className="mr-1 md:mr-2" size={14} />
              Devis
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-red-600 text-xs md:text-sm">
              <Euro className="mr-1 md:mr-2" size={14} />
              Factures
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-green-600 text-xs md:text-sm">
              <TrendingUp className="mr-1 md:mr-2" size={14} />
              <span className="hidden sm:inline">Rapports CA</span>
              <span className="sm:hidden">CA</span>
            </TabsTrigger>
            <TabsTrigger
              value="dossiers"
              className="data-[state=active]:bg-violet-600 text-xs md:text-sm"
              data-testid="tab-dossiers"
              onClick={() => fetchDossiers(dossierFilterCollab, dossierFilterType)}
            >
              <Tag className="mr-1 md:mr-2" size={14} />
              <span className="hidden sm:inline">Dossiers</span>
              <span className="sm:hidden">Doss</span>
            </TabsTrigger>
            {isMainAdmin && (
              <TabsTrigger value="messages" className="data-[state=active]:bg-red-600 text-xs md:text-sm">
                <MessageSquare className="mr-1 md:mr-2" size={14} />
                <span className="hidden sm:inline">Messages</span>
                <span className="sm:hidden">Msg</span>
              </TabsTrigger>
            )}
            {isMainAdmin && (
              <TabsTrigger value="users" className="data-[state=active]:bg-red-600 text-xs md:text-sm">
                <Users className="mr-1 md:mr-2" size={14} />
                <span className="hidden sm:inline">Utilisateurs</span>
                <span className="sm:hidden">Users</span>
              </TabsTrigger>
            )}
            {isMainAdmin && (
              <TabsTrigger value="marketing" className="data-[state=active]:bg-red-600 text-xs md:text-sm">
                <Send className="mr-1 md:mr-2" size={14} />
                <span className="hidden sm:inline">Marketing</span>
                <span className="sm:hidden">Promo</span>
              </TabsTrigger>
            )}
            {isMainAdmin && (
              <TabsTrigger value="team" className="data-[state=active]:bg-blue-600 text-xs md:text-sm">
                <Shield className="mr-1 md:mr-2" size={14} />
                <span className="hidden sm:inline">Equipe</span>
                <span className="sm:hidden">Team</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Cash Register */}
          <TabsContent value="inbox">
            <div className="grid lg:grid-cols-3 gap-4">
              {/* Liste des notifications */}
              <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-1">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Bell size={18} className="text-red-500" />
                    Boite de reception
                    {unreadCount > 0 && <span className="bg-red-600 text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>}
                  </CardTitle>
                  {unreadCount > 0 && (
                    <Button size="sm" variant="ghost" onClick={markAllRead} className="text-xs" data-testid="mark-all-read-btn">
                      Tout lire
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-0 max-h-[700px] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-center py-12 text-zinc-500 text-sm">Aucune notification</p>
                  ) : (
                    <div className="divide-y divide-zinc-800">
                      {sortByTreatment(notifications).map((n) => (
                        <button
                          key={n.id}
                          onClick={() => openNotif(n)}
                          className={`w-full text-left p-3 hover:bg-zinc-800/50 transition ${!n.is_read ? 'bg-zinc-800/30 border-l-2 border-red-500' : ''} ${selectedNotif?.id === n.id ? 'bg-zinc-800' : ''}`}
                          data-testid={`notif-${n.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              n.category === 'appointment' ? 'bg-blue-600/30 text-blue-300' :
                              n.category === 'wash' ? 'bg-cyan-600/30 text-cyan-300' :
                              n.category === 'towing' ? 'bg-orange-600/30 text-orange-300' :
                              n.category === 'quote' ? 'bg-purple-600/30 text-purple-300' :
                              n.category === 'contact' ? 'bg-yellow-600/30 text-yellow-300' :
                              n.category === 'client_register' ? 'bg-green-600/30 text-green-300' :
                              'bg-zinc-700 text-zinc-300'
                            }`}>
                              {n.category}
                            </span>
                            {!n.is_read && <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></span>}
                          </div>
                          <p className={`mt-1.5 text-sm ${!n.is_read ? 'text-white font-semibold' : 'text-zinc-300'}`}>{n.subject}</p>
                          <p className="text-xs text-zinc-500 mt-1">{new Date(n.created_at).toLocaleString('fr-FR')}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Detail */}
              <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white text-base">
                    {selectedNotif ? selectedNotif.subject : 'Selectionnez une notification'}
                  </CardTitle>
                  {selectedNotif && (
                    <Button size="sm" variant="ghost" onClick={() => deleteNotif(selectedNotif.id)} className="text-red-400 hover:text-red-300" data-testid="delete-notif">
                      <Trash2 size={14} />
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {selectedNotif ? (
                    <div>
                      <p className="text-xs text-zinc-500 mb-3">
                        {new Date(selectedNotif.created_at).toLocaleString('fr-FR')} - {selectedNotif.category}
                      </p>
                      <div
                        className="bg-zinc-950/50 rounded-lg p-4"
                        dangerouslySetInnerHTML={{ __html: selectedNotif.html }}
                      />
                    </div>
                  ) : (
                    <div className="text-center py-16 text-zinc-500">
                      <AlertCircle size={48} className="mx-auto mb-3 opacity-30" />
                      <p>Cliquez sur une notification pour voir les details</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="cash">
            <div className="grid gap-6">
              {/* Cash Session Status */}
              <Card className={`border-2 ${cashSession ? 'border-green-500 bg-green-500/5' : 'border-zinc-700 bg-zinc-900/50'}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Banknote className={cashSession ? 'text-green-400' : 'text-zinc-400'} size={24} />
                    {cashSession ? 'Caisse Ouverte' : 'Caisse Fermée'}
                  </CardTitle>
                  {cashSession ? (
                    <Button onClick={() => setShowCloseCashModal(true)} variant="destructive" className="bg-red-600 hover:bg-red-700">
                      <Lock className="mr-2" size={16} />
                      Fermer la caisse
                    </Button>
                  ) : (
                    <Button onClick={() => setShowOpenCashModal(true)} className="bg-green-600 hover:bg-green-700">
                      <Unlock className="mr-2" size={16} />
                      Ouvrir la caisse
                    </Button>
                  )}
                </CardHeader>
                {cashSession && (
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-zinc-800/50 rounded-lg p-4">
                        <p className="text-zinc-400 text-sm">Fond de caisse</p>
                        <p className="text-2xl font-bold text-white">{cashSession.initial_cash?.toFixed(2)}€</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-4">
                        <p className="text-zinc-400 text-sm">Total ventes</p>
                        <p className="text-2xl font-bold text-green-400">{cashSession.total_sales?.toFixed(2)}€</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-4">
                        <p className="text-zinc-400 text-sm">Espèces</p>
                        <p className="text-2xl font-bold text-yellow-400">{cashSession.total_cash?.toFixed(2)}€</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-4">
                        <p className="text-zinc-400 text-sm">Carte</p>
                        <p className="text-2xl font-bold text-blue-400">{cashSession.total_card?.toFixed(2)}€</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-lg p-4">
                        <p className="text-zinc-400 text-sm">Nb ventes</p>
                        <p className="text-2xl font-bold text-white">{cashSession.sales_count}</p>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-zinc-400">
                      Ouverte par {cashSession.opened_by_name} le {new Date(cashSession.opened_at).toLocaleString('fr-FR')}
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* New Sale Button */}
              {cashSession && (
                <Button 
                  onClick={() => setShowNewSaleModal(true)} 
                  className="bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                  data-testid="new-sale-btn"
                >
                  <Plus className="mr-2" size={24} />
                  Nouvelle Vente
                </Button>
              )}

              {/* Recent Sales */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Receipt className="text-green-400" size={20} />
                    Ventes récentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">N° Vente</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Date</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Source</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Caissier</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Client</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Total</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Paiement</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Statut</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashSales.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="py-8 text-center text-zinc-500">
                              Aucune vente enregistrée
                            </td>
                          </tr>
                        ) : (
                          sortByTreatment(cashSales).map((sale) => (
                            <tr key={sale.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${sale.status === 'cancelled' ? 'opacity-50' : ''}`}>
                              <td className="py-3 px-4 text-white font-mono">{sale.sale_number}</td>
                              <td className="py-3 px-4 text-zinc-300">
                                {new Date(sale.created_at).toLocaleString('fr-FR')}
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={sale.register_source === 'internet' ? 'bg-blue-600' : 'bg-red-600'}>
                                  {sale.register_source === 'internet' ? 'Internet' : 'Comptoir'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-zinc-300">{sale.cashier_name}</td>
                              <td className="py-3 px-4 text-zinc-300">{sale.client_name || '-'}</td>
                              <td className="py-3 px-4 text-green-400 font-bold">{sale.total.toFixed(2)}€</td>
                              <td className="py-3 px-4">
                                <Badge className={
                                  sale.payment_method === 'cash' ? 'bg-yellow-600' :
                                  sale.payment_method === 'card' ? 'bg-blue-600' :
                                  sale.payment_method === 'transfer' ? 'bg-green-600' : 'bg-zinc-600'
                                }>
                                  {sale.payment_method === 'cash' ? 'Espèces' :
                                   sale.payment_method === 'card' ? 'Carte' :
                                   sale.payment_method === 'transfer' ? 'Virement' : 'Mixte'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={sale.status === 'completed' ? 'bg-green-600' : 'bg-red-600'}>
                                  {sale.status === 'completed' ? 'Validée' : 'Annulée'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-1 flex-wrap">
                                  <Button size="sm" variant="outline" onClick={() => printThermalReceipt(sale)} className="text-zinc-300" title="Ticket thermique">
                                    <Printer size={14} />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => printDuplicataReceipt(sale)} className="text-orange-400" title="Duplicata ticket">
                                    <Copy size={14} />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => printInvoice(sale)} className="text-blue-400" title="Facture PDF">
                                    <FileText size={14} />
                                  </Button>
                                  {sale.client_email && (
                                    <Button size="sm" variant="outline" onClick={() => sendInvoiceByEmail(sale)} className="text-green-400" title="Envoyer par email">
                                      <Mail size={14} />
                                    </Button>
                                  )}
                                  {sale.status === 'completed' && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => startEditSale(sale)} className="text-yellow-400" title="Corriger">
                                        <Wrench size={14} />
                                      </Button>
                                      <Button size="sm" variant="destructive" onClick={() => cancelSale(sale.id)} title="Annuler">
                                        <X size={14} />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Reports */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="text-cyan-400" size={20} />
                      Rapports de caisse
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button 
                        variant={cashReportType === 'daily' ? 'default' : 'outline'} 
                        onClick={() => fetchCashReport('daily')}
                        className={cashReportType === 'daily' ? 'bg-cyan-600' : ''}
                      >
                        Journalier
                      </Button>
                      <Button 
                        variant={cashReportType === 'weekly' ? 'default' : 'outline'} 
                        onClick={() => fetchCashReport('weekly')}
                        className={cashReportType === 'weekly' ? 'bg-cyan-600' : ''}
                      >
                        Hebdomadaire
                      </Button>
                      <Button 
                        variant={cashReportType === 'monthly' ? 'default' : 'outline'} 
                        onClick={() => fetchCashReport('monthly')}
                        className={cashReportType === 'monthly' ? 'bg-cyan-600' : ''}
                      >
                        Mensuel
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {cashReport && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-zinc-800/50 rounded-lg p-4">
                          <p className="text-zinc-400 text-sm">Total des ventes</p>
                          <p className="text-3xl font-bold text-green-400">{cashReport.total_sales?.toFixed(2)}€</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-4">
                          <p className="text-zinc-400 text-sm">Nombre de ventes</p>
                          <p className="text-3xl font-bold text-white">{cashReport.sales_count || cashReport.total_count}</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-4">
                          <p className="text-zinc-400 text-sm">Espèces</p>
                          <p className="text-3xl font-bold text-yellow-400">{cashReport.total_cash?.toFixed(2)}€</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-4">
                          <p className="text-zinc-400 text-sm">Carte bancaire</p>
                          <p className="text-3xl font-bold text-blue-400">{cashReport.total_card?.toFixed(2)}€</p>
                        </div>
                      </div>
                      
                      {cashReport.cancelled_count > 0 && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <p className="text-red-400">
                            {cashReport.cancelled_count} vente(s) annulée(s) - Total: {cashReport.cancelled_total?.toFixed(2)}€
                          </p>
                        </div>
                      )}

                      {/* Daily breakdown for weekly/monthly */}
                      {cashReport.daily_breakdown && (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-zinc-800">
                                <th className="text-left py-2 px-4 text-zinc-400">Date</th>
                                <th className="text-right py-2 px-4 text-zinc-400">Ventes</th>
                                <th className="text-right py-2 px-4 text-zinc-400">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cashReport.daily_breakdown.map((day, idx) => (
                                <tr key={idx} className="border-b border-zinc-800/50">
                                  <td className="py-2 px-4 text-white">
                                    {day.day_name ? `${day.day_name} (${day.date})` : day.date}
                                  </td>
                                  <td className="py-2 px-4 text-right text-zinc-300">{day.count}</td>
                                  <td className="py-2 px-4 text-right text-green-400 font-bold">{day.total.toFixed(2)}€</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pending Balances — acomptes restants a encaisser en magasin */}
          <TabsContent value="balances">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card className="bg-amber-500/10 border-amber-500/30" data-testid="balances-summary-today">
                  <CardContent className="p-4">
                    <p className="text-zinc-400 text-xs">A encaisser aujourd'hui</p>
                    <p className="text-3xl font-bold text-amber-400 mt-1">{(pendingBalances.summary?.today_amount || 0).toFixed(2)}€</p>
                    <p className="text-zinc-500 text-xs mt-1">{pendingBalances.summary?.today_count || 0} client(s) au programme</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-500/10 border-green-500/30" data-testid="balances-summary-total">
                  <CardContent className="p-4">
                    <p className="text-zinc-400 text-xs">Total a encaisser (tous RDV futurs)</p>
                    <p className="text-3xl font-bold text-green-400 mt-1">{(pendingBalances.summary?.total_to_collect || 0).toFixed(2)}€</p>
                    <p className="text-zinc-500 text-xs mt-1">{pendingBalances.summary?.total_count || 0} dossier(s) en cours</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <p className="text-zinc-400 text-xs">Acomptes encaisses en ligne</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {(pendingBalances.items || []).reduce((sum, i) => sum + (i.deposit_paid || 0), 0).toFixed(2)}€
                    </p>
                    <p className="text-zinc-500 text-xs mt-1">Total deja securise</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Euro className="text-amber-500" size={20} />
                    Soldes restants a encaisser en magasin
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(pendingBalances.items || []).length === 0 ? (
                    <p className="text-zinc-500 text-center py-8" data-testid="balances-empty">
                      Aucun solde a encaisser. Tous les acomptes en ligne ont ete soldes en magasin.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-800 text-zinc-400">
                            <th className="text-left py-2 px-2">Date / Heure</th>
                            <th className="text-left py-2 px-2">Client</th>
                            <th className="text-left py-2 px-2 hidden md:table-cell">Vehicule</th>
                            <th className="text-left py-2 px-2">Service</th>
                            <th className="text-right py-2 px-2">Acompte</th>
                            <th className="text-right py-2 px-2">A encaisser</th>
                            <th className="text-center py-2 px-2">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingBalances.items.map((item, idx) => (
                            <tr key={`${item.type}-${item.reference}-${idx}`} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`balance-row-${idx}`}>
                              <td className="py-3 px-2 text-zinc-300">
                                <div>{item.scheduled_date}</div>
                                <div className="text-xs text-zinc-500">{item.scheduled_time}</div>
                              </td>
                              <td className="py-3 px-2">
                                <div className="text-white font-medium">{item.client_name || '-'}</div>
                                <div className="text-xs text-zinc-500">{item.client_phone || item.client_email || ''}</div>
                              </td>
                              <td className="py-3 px-2 text-zinc-400 hidden md:table-cell">{item.vehicle || '-'}</td>
                              <td className="py-3 px-2">
                                <Badge variant="outline" className="border-zinc-600 text-xs">{item.type_label}</Badge>
                                <div className="text-xs text-zinc-400 mt-1">{item.service_detail}</div>
                              </td>
                              <td className="py-3 px-2 text-right">
                                <span className="text-green-400 font-mono text-xs">{item.deposit_paid.toFixed(2)}€</span>
                              </td>
                              <td className="py-3 px-2 text-right">
                                <span className="text-amber-400 font-bold tabular-nums">{item.remaining_to_collect.toFixed(2)}€</span>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex flex-col sm:flex-row gap-1 justify-center">
                                  <Button size="sm" onClick={() => markBalanceCollected(item, 'cash')} className="bg-green-600 hover:bg-green-700 text-xs" data-testid={`mark-cash-${idx}`}>Espèces</Button>
                                  <Button size="sm" onClick={() => markBalanceCollected(item, 'card')} className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`mark-card-${idx}`}>CB</Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Appointments */}
          <TabsContent value="appointments">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Gestion des rendez-vous</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Client</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Service</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Véhicule</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Date</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Statut</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Attribué à</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortByTreatment(appointments).map((apt) => (
                        <tr key={apt.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="py-3 px-4">
                            <button 
                              className="text-left hover:bg-zinc-700/50 p-2 rounded-lg transition-colors w-full"
                              onClick={async () => {
                                // Récupérer l'historique et afficher la fiche client
                                try {
                                  toast.info('Chargement du profil client...');
                                  const historyRes = await axios.get(`${API}/admin/users/${apt.user_id}/history`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  setSelectedUserForDetails({
                                    id: apt.user_id,
                                    name: apt.user_name,
                                    email: apt.user_email,
                                    phone: apt.user_phone,
                                    history: historyRes.data
                                  });
                                } catch (error) {
                                  // Si erreur, afficher quand même avec les infos basiques
                                  setSelectedUserForDetails({
                                    id: apt.user_id,
                                    name: apt.user_name,
                                    email: apt.user_email,
                                    phone: apt.user_phone,
                                    history: null
                                  });
                                }
                              }}
                              title="Cliquer pour voir la fiche client"
                            >
                              <p className="text-white font-medium hover:text-red-400 transition-colors">{apt.user_name}</p>
                              <p className="text-zinc-500 text-sm">{apt.user_email}</p>
                              {apt.user_phone && <p className="text-zinc-600 text-xs">{apt.user_phone}</p>}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-zinc-300">{apt.service_name}</td>
                          <td className="py-3 px-4">
                            <p className="text-zinc-300">{apt.vehicle_brand} {apt.vehicle_model}</p>
                            <p className="text-zinc-500 font-mono text-sm">{apt.vehicle_plate}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-white">{new Date(apt.date).toLocaleDateString('fr-FR')}</p>
                            <p className="text-zinc-500 text-sm">{apt.time_slot}</p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={statusColors[apt.status]}>{apt.status}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <DossierAssignmentTag
                              dossierType="appointment"
                              dossierId={apt.id}
                              assignedTo={apt.assigned_to}
                              compact
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2 flex-wrap">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => updateAppointmentStatus(apt.id, 'confirmed')}
                                title="Confirmer"
                              >
                                <Check size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-600 text-blue-400 hover:bg-blue-700 hover:text-white"
                                onClick={() => openEditApptAdmin(apt)}
                                title="Modifier ce RDV"
                                data-testid={`admin-edit-appt-${apt.id}`}
                              >
                                <Edit size={14} className="mr-1" /> Modifier
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-zinc-700"
                                onClick={() => handleCompleteAppointment(apt)}
                              >
                                Terminé
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-blue-600 text-blue-500 hover:bg-blue-600 hover:text-white"
                                onClick={() => downloadClientSheet(apt)}
                                title="Télécharger fiche client PDF"
                              >
                                <Download size={14} className="mr-1" />
                                Fiche
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => updateAppointmentStatus(apt.id, 'cancelled')}
                                title="Annuler"
                              >
                                <X size={14} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-800 text-red-500 hover:bg-red-900 hover:text-red-300"
                                onClick={() => deleteAppointment(apt.id)}
                                title="Supprimer définitivement"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {appointments.length === 0 && (
                    <p className="text-center py-8 text-zinc-500">Aucun rendez-vous</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Wash Bookings */}
          <TabsContent value="wash">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Droplets className="text-blue-400" size={20} />
                  Réservations Lavage
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={washDateFilter}
                    onChange={(e) => setWashDateFilter(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white w-40"
                  />
                  {washDateFilter && (
                    <Button size="sm" variant="outline" onClick={() => setWashDateFilter('')} className="border-zinc-700">
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Client</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Date & Heure</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Formule</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Véhicule</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Statut</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortByTreatment(filteredWashBookings).map((booking) => (
                        <tr key={booking.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="py-3 px-2">
                            <p className="text-white font-medium">{booking.user_name}</p>
                            <p className="text-zinc-500 text-xs">{booking.user_phone}</p>
                          </td>
                          <td className="py-3 px-2">
                            <p className="text-white">{new Date(booking.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                            <p className="text-red-400 font-bold">{booking.time_slot}</p>
                          </td>
                          <td className="py-3 px-2">
                            <Badge className="bg-blue-600">{booking.wash_type?.toUpperCase()}</Badge>
                          </td>
                          <td className="py-3 px-2">
                            <p className="text-zinc-300">{booking.vehicle_type}</p>
                            {booking.vehicle_info && (
                              <p className="text-zinc-500 text-xs">{booking.vehicle_info}</p>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <Badge className={
                              booking.status === 'confirmed' ? 'bg-green-600' :
                              booking.status === 'completed' ? 'bg-blue-600' :
                              booking.status === 'cancelled' ? 'bg-red-600' : 'bg-yellow-600'
                            }>
                              {booking.status === 'confirmed' ? 'Confirmé' :
                               booking.status === 'completed' ? 'Terminé' :
                               booking.status === 'cancelled' ? 'Annulé' : booking.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-600 text-blue-400 hover:bg-blue-700 hover:text-white"
                                onClick={() => openEditWashAdmin(booking)}
                                title="Modifier ce lavage"
                                data-testid={`admin-edit-wash-${booking.id}`}
                              >
                                <Edit size={14} />
                              </Button>
                              {booking.status === 'confirmed' && (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => updateWashBookingStatus(booking.id, 'completed')}
                                  >
                                    <Check size={14} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => updateWashBookingStatus(booking.id, 'cancelled')}
                                  >
                                    <X size={14} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredWashBookings.length === 0 && (
                    <p className="text-center py-8 text-zinc-500">
                      {washDateFilter ? 'Aucune réservation ce jour' : 'Aucune réservation de lavage'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Slots Overview Grid */}
            {washDateFilter && washSlotsOverview && (
              <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="text-yellow-400" size={20} />
                    Créneaux du {new Date(washDateFilter).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={blockAllDay} className="bg-red-600 hover:bg-red-700">
                      <Lock size={14} className="mr-1" /> Bloquer tout
                    </Button>
                    <Button size="sm" onClick={unblockAllDay} variant="outline" className="border-zinc-700">
                      <Unlock size={14} className="mr-1" /> Débloquer tout
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-600 rounded"></div>
                      <span className="text-zinc-400">Disponible ({washSlotsOverview.stats.available})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-600 rounded"></div>
                      <span className="text-zinc-400">Réservé ({washSlotsOverview.stats.booked})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-600 rounded"></div>
                      <span className="text-zinc-400">Bloqué ({washSlotsOverview.stats.blocked})</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-11 gap-2">
                    {washSlotsOverview.slots.map((slot) => (
                      <div
                        key={slot.time}
                        className={`p-2 rounded text-center text-sm transition-all
                          ${slot.status === 'available' ? 'bg-green-600/20 border border-green-600 hover:bg-green-600/40 cursor-pointer' :
                            slot.status === 'blocked' ? 'bg-red-600/20 border border-red-600 hover:bg-red-600/40 cursor-pointer' :
                            'bg-blue-600/20 border border-blue-600'}`}
                        onClick={() => {
                          if (slot.status === 'available') blockSlot(slot.time);
                          else if (slot.status === 'blocked') unblockSlot(slot.time);
                        }}
                        title={
                          slot.status === 'available' ? 'Cliquer pour bloquer' :
                          slot.status === 'blocked' ? 'Cliquer pour débloquer' :
                          `Réservé: ${slot.user_name} - ${slot.wash_type}`
                        }
                      >
                        <p className="text-white font-bold">{slot.time}</p>
                        {(slot.status === 'confirmed' || slot.status === 'pending_payment') && (
                          <div>
                            <p className="text-xs text-zinc-400 truncate">{slot.user_name?.split(' ')[0]}</p>
                            <p className="text-xs text-blue-400">{slot.wash_type?.toUpperCase()}</p>
                            <div className="flex gap-1 mt-1 justify-center">
                              <button
                                onClick={(e) => { e.stopPropagation(); updateWashBookingStatus(slot.booking_id, 'completed'); }}
                                className="bg-green-600 hover:bg-green-700 text-white text-xs px-1.5 py-0.5 rounded"
                                title="Marquer terminé"
                              >
                                <Check size={10} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); updateWashBookingStatus(slot.booking_id, 'cancelled'); }}
                                className="bg-orange-600 hover:bg-orange-700 text-white text-xs px-1.5 py-0.5 rounded"
                                title="Annuler"
                              >
                                <X size={10} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteWashBooking(slot.booking_id); }}
                                className="bg-red-700 hover:bg-red-800 text-white text-xs px-1.5 py-0.5 rounded"
                                title="Supprimer"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </div>
                        )}
                        {slot.status === 'blocked' && (
                          <Lock size={12} className="mx-auto text-red-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Quotes */}
          <TabsContent value="quotes">
            {/* Quote Form Modal */}
            {showQuoteForm && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <Card className="bg-zinc-900 border-zinc-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white">{editingQuote ? 'Modifier le devis' : 'Nouveau devis'}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => { setShowQuoteForm(false); resetQuoteForm(); }}>
                      <X size={20} />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={createOrUpdateQuote} className="space-y-4">
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-zinc-300">Nom *</Label>
                          <Input
                            required
                            value={quoteForm.client_name}
                            onChange={(e) => setQuoteForm({...quoteForm, client_name: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Prénom</Label>
                          <Input
                            value={quoteForm.client_firstname}
                            onChange={(e) => setQuoteForm({...quoteForm, client_firstname: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Société</Label>
                          <Input
                            value={quoteForm.client_company}
                            onChange={(e) => setQuoteForm({...quoteForm, client_company: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-zinc-300">Email</Label>
                          <Input
                            type="email"
                            value={quoteForm.client_email}
                            onChange={(e) => setQuoteForm({...quoteForm, client_email: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Téléphone</Label>
                          <Input
                            value={quoteForm.client_phone}
                            onChange={(e) => setQuoteForm({...quoteForm, client_phone: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Validité (jours)</Label>
                          <Input
                            type="number"
                            min="1"
                            value={quoteForm.validity_days}
                            onChange={(e) => setQuoteForm({...quoteForm, validity_days: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-300">Adresse</Label>
                          <Input
                            value={quoteForm.client_address}
                            onChange={(e) => setQuoteForm({...quoteForm, client_address: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-zinc-300">Immatriculation</Label>
                            <Input
                              value={quoteForm.vehicle_plate}
                              onChange={(e) => setQuoteForm({...quoteForm, vehicle_plate: e.target.value.toUpperCase()})}
                              className="bg-zinc-800 border-zinc-700 text-white mt-1"
                              placeholder="AA-123-BB"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Véhicule</Label>
                            <Input
                              value={quoteForm.vehicle_model}
                              onChange={(e) => setQuoteForm({...quoteForm, vehicle_model: e.target.value})}
                              className="bg-zinc-800 border-zinc-700 text-white mt-1"
                              placeholder="Marque Modèle"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Quote Items */}
                      <div>
                        <Label className="text-zinc-300 mb-2 block">Lignes du devis *</Label>
                        <div className="grid grid-cols-12 gap-2 mb-2 text-zinc-400 text-xs">
                          <span className="col-span-4">Description</span>
                          <span className="col-span-1 text-center">Qté</span>
                          <span className="col-span-2 text-center">P.U. HT</span>
                          <span className="col-span-2 text-center">P.U. TTC</span>
                          <span className="col-span-2 text-center">Total HT</span>
                          <span className="col-span-1"></span>
                        </div>
                        <div className="space-y-2">
                          {quoteForm.items.map((item, idx) => {
                            const priceHT = parseFloat(item.unit_price) || 0;
                            const priceTTC = priceHT * 1.2;
                            const totalHT = (item.quantity || 1) * priceHT;
                            return (
                              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-4">
                                  <Input
                                    placeholder="Description"
                                    value={item.description}
                                    onChange={(e) => {
                                      const newItems = [...quoteForm.items];
                                      newItems[idx].description = e.target.value;
                                      setQuoteForm({...quoteForm, items: newItems});
                                    }}
                                    className="bg-zinc-800 border-zinc-700 text-white"
                                    required
                                  />
                                </div>
                                <div className="col-span-1">
                                  <Input
                                    type="number"
                                    min="1"
                                    placeholder="Qté"
                                    value={item.quantity}
                                    onChange={(e) => {
                                      const newItems = [...quoteForm.items];
                                      newItems[idx].quantity = parseInt(e.target.value) || 1;
                                      newItems[idx].total = newItems[idx].quantity * newItems[idx].unit_price;
                                      setQuoteForm({...quoteForm, items: newItems});
                                    }}
                                    className="bg-zinc-800 border-zinc-700 text-white text-center"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="P.U. HT"
                                    value={item.unit_price === 0 || item.unit_price === '' ? '' : item.unit_price}
                                    onChange={(e) => {
                                      const newItems = [...quoteForm.items];
                                      const v = Math.max(0, parseFloat(e.target.value) || 0);
                                      newItems[idx].unit_price = v;
                                      newItems[idx].total = newItems[idx].quantity * v;
                                      setQuoteForm({...quoteForm, items: newItems});
                                    }}
                                    className="bg-zinc-800 border-zinc-700 text-white text-center"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="P.U. TTC"
                                    value={priceTTC === 0 ? '' : priceTTC.toFixed(2)}
                                    onChange={(e) => {
                                      // User types TTC → recompute HT = TTC / 1.2
                                      const newItems = [...quoteForm.items];
                                      const ttc = Math.max(0, parseFloat(e.target.value) || 0);
                                      const ht = +(ttc / 1.2).toFixed(2);
                                      newItems[idx].unit_price = ht;
                                      newItems[idx].total = newItems[idx].quantity * ht;
                                      setQuoteForm({...quoteForm, items: newItems});
                                    }}
                                    className="bg-zinc-800 border-zinc-700 text-white text-center"
                                  />
                                </div>
                                <div className="col-span-2 text-center text-white font-medium">
                                  {totalHT.toFixed(2)} €
                                </div>
                                <div className="col-span-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (quoteForm.items.length > 1) {
                                        const newItems = quoteForm.items.filter((_, i) => i !== idx);
                                        setQuoteForm({...quoteForm, items: newItems});
                                      }
                                    }}
                                    className="text-red-500 hover:text-red-400"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setQuoteForm({...quoteForm, items: [...quoteForm.items, { description: '', quantity: 1, unit_price: '', total: 0 }]})}
                          className="mt-2 border-zinc-600"
                        >
                          <Plus size={14} className="mr-1" /> Ajouter une ligne
                        </Button>
                      </div>

                      {/* Totals */}
                      <div className="bg-zinc-800 rounded-lg p-4">
                        <div className="flex justify-between text-zinc-400 mb-2">
                          <span>Sous-total HT</span>
                          <span>{quoteForm.items.reduce((sum, item) => sum + (item.total || 0), 0).toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-zinc-400 mb-2">
                          <span>TVA 20%</span>
                          <span>{(quoteForm.items.reduce((sum, item) => sum + (item.total || 0), 0) * 0.2).toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-white font-bold text-lg">
                          <span>Total TTC</span>
                          <span>{(quoteForm.items.reduce((sum, item) => sum + (item.total || 0), 0) * 1.2).toFixed(2)} €</span>
                        </div>
                      </div>

                      <div>
                        <Label className="text-zinc-300">Notes / Conditions</Label>
                        <textarea
                          value={quoteForm.notes}
                          onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})}
                          className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md p-3 mt-1"
                          rows={3}
                          placeholder="Conditions particulières, délais, etc."
                        />
                      </div>

                      <div className="flex gap-3 justify-end">
                        <Button type="button" variant="outline" className="border-zinc-600" onClick={() => { setShowQuoteForm(false); resetQuoteForm(); }}>
                          Annuler
                        </Button>
                        <Button type="submit" className="bg-red-600 hover:bg-red-700">
                          {editingQuote ? 'Modifier' : 'Créer le devis'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Professional Quotes Section */}
            <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Devis professionnels</CardTitle>
                <Button 
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => { resetQuoteForm(); setShowQuoteForm(true); }}
                  data-testid="new-quote-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Nouveau devis
                </Button>
              </CardHeader>
              <CardContent>
                {professionalQuotes.length > 0 ? (
                  <div className="space-y-3">
                    {sortByTreatment(professionalQuotes).map((quote) => (
                      <Card key={quote.id} className="bg-zinc-800/30 border-zinc-700">
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <span className="text-red-500 font-mono font-bold">{quote.quote_number}</span>
                                <Badge className={
                                  quote.status === 'draft' ? 'bg-gray-500' :
                                  quote.status === 'sent' ? 'bg-blue-500' :
                                  quote.status === 'accepted' ? 'bg-green-500' :
                                  quote.status === 'rejected' ? 'bg-red-500' :
                                  quote.status === 'converted' ? 'bg-purple-500' :
                                  'bg-orange-500'
                                }>
                                  {quote.status === 'draft' ? 'Brouillon' :
                                   quote.status === 'sent' ? 'Envoyé' :
                                   quote.status === 'accepted' ? 'Accepté' :
                                   quote.status === 'rejected' ? 'Refusé' :
                                   quote.status === 'converted' ? 'Converti' :
                                   quote.status}
                                </Badge>
                                <span className="text-white font-bold">{quote.company || `${quote.firstname || ''} ${quote.name}`}</span>
                                <DossierAssignmentTag
                                  dossierType="admin_quote"
                                  dossierId={quote.id}
                                  assignedTo={quote.assigned_to}
                                  compact
                                />
                              </div>
                              <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                                {quote.email && <span>📧 {quote.email}</span>}
                                {quote.phone && <span>📱 {quote.phone}</span>}
                                {quote.vehicle_plate && <span>🚗 {quote.vehicle_plate}</span>}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                                <span className="text-zinc-500">Créé le {new Date(quote.created_at).toLocaleDateString('fr-FR')}</span>
                                <span className="text-orange-400">Valide jusqu'au {new Date(quote.validity_date).toLocaleDateString('fr-FR')}</span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className="text-2xl font-bold text-white">{quote.total.toFixed(2)} €</span>
                              <div className="flex flex-wrap gap-2">
                                <Button size="sm" variant="outline" className="border-zinc-600" onClick={() => printQuotePDF(quote)}>
                                  <Printer size={14} className="mr-1" /> PDF
                                </Button>
                                <Button size="sm" variant="outline" className="border-zinc-600" onClick={() => startEditQuote(quote)}>
                                  <Edit size={14} className="mr-1" /> Modifier
                                </Button>
                                {quote.status !== 'converted' && (
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => convertQuoteToInvoice(quote)}>
                                    Convertir en facture
                                  </Button>
                                )}
                                <Button size="sm" variant="destructive" onClick={() => deleteQuote(quote.id)}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                              {quote.status === 'draft' && (
                                <div className="flex gap-1 mt-1">
                                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => sendQuoteToClient(quote)}>
                                    <Send size={14} className="mr-1" /> Envoyer au client
                                  </Button>
                                </div>
                              )}
                              {quote.status === 'sent' && (
                                <div className="flex gap-1 mt-1">
                                  <span className="text-blue-400 text-sm mr-2">📧 Envoyé</span>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateQuoteProfessionalStatus(quote.id, 'accepted')}>
                                    Accepté
                                  </Button>
                                  <Button size="sm" variant="destructive" onClick={() => updateQuoteProfessionalStatus(quote.id, 'rejected')}>
                                    Refusé
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-zinc-500">Aucun devis professionnel créé</p>
                )}
              </CardContent>
            </Card>

            {/* Client Quote Requests Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Demandes de devis clients</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sortByTreatment(quotes).map((quote) => (
                    <Card key={quote.id} className="bg-zinc-800/30 border-zinc-700">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-white font-bold">{quote.name}</h4>
                              <Badge className={statusColors[quote.status]}>{quote.status}</Badge>
                            </div>
                            <p className="text-zinc-400">{quote.email} • {quote.phone}</p>
                            <p className="text-zinc-300 mt-2">
                              <strong>{quote.service_type}</strong> - {quote.vehicle_brand} {quote.vehicle_model}
                            </p>
                            <p className="text-zinc-500 mt-2">{quote.description}</p>
                            
                            {/* Attachments */}
                            {quote.attachments && quote.attachments.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-zinc-700">
                                <p className="text-zinc-400 text-sm mb-2">📎 Pièces jointes ({quote.attachments.length})</p>
                                <div className="flex flex-wrap gap-3">
                                  {quote.attachments.map((url, idx) => {
                                    // Handle both old (/api/uploads/) and new (/api/files/) URLs
                                    const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
                                    
                                    return isImage ? (
                                      <a 
                                        key={idx}
                                        href={fullUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block border border-zinc-600 rounded-lg overflow-hidden hover:border-red-500 transition-colors"
                                        title="Cliquer pour ouvrir en grand"
                                      >
                                        <img 
                                          src={fullUrl} 
                                          alt={`Pièce jointe ${idx + 1}`}
                                          className="w-24 h-24 object-cover"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                        <div className="hidden w-24 h-24 bg-zinc-700 items-center justify-center text-zinc-400 text-xs text-center p-1">
                                          Image perdue (ancien stockage)
                                        </div>
                                      </a>
                                    ) : (
                                      <a 
                                        key={idx}
                                        href={fullUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 bg-zinc-700/50 hover:bg-zinc-600/50 text-zinc-300 text-sm px-4 py-3 rounded-lg transition-colors"
                                      >
                                        📄 Fichier {idx + 1}
                                        <span className="text-xs text-zinc-500">(PDF)</span>
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            {/* Download client request sheet */}
                            <Button 
                              size="sm"
                              variant="outline"
                              className="border-zinc-600"
                              onClick={() => printClientRequestSheet(quote)}
                            >
                              <Download size={14} className="mr-1" /> Fiche client
                            </Button>
                            {quote.status === 'pending' && (
                              <Button 
                                size="sm"
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => createQuoteFromRequest(quote)}
                              >
                                <FileText size={14} className="mr-1" /> Créer le devis
                              </Button>
                            )}
                            {quote.status === 'pending' && (
                              <Button 
                                size="sm"
                                variant="destructive"
                                onClick={() => updateQuoteStatus(quote.id, 'rejected')}
                              >
                                Refuser
                              </Button>
                            )}
                            {quote.status === 'in_progress' && (
                              <Badge className="bg-blue-600">En cours de traitement</Badge>
                            )}
                            {quote.status === 'quote_sent' && (
                              <Badge className="bg-green-600">Devis envoyé</Badge>
                            )}
                            {quote.linked_quote_id && (
                              <span className="text-xs text-zinc-500">Devis lié</span>
                            )}
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteQuoteRequest(quote.id)}
                            >
                              <Trash2 size={14} className="mr-1" /> Supprimer
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {quotes.length === 0 && (
                    <p className="text-center py-8 text-zinc-500">Aucune demande de devis client</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices */}
          <TabsContent value="invoices">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Gestion des factures</CardTitle>
                <Button 
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => { resetInvoiceForm(); setShowInvoiceForm(!showInvoiceForm); }}
                >
                  <Plus size={16} className="mr-2" />
                  Nouvelle facture
                </Button>
              </CardHeader>
              <CardContent>
                {/* Credit Note Modal */}
                {showCreditNoteForm && creditNoteInvoice && (
                  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md mx-4">
                      <CardHeader>
                        <CardTitle className="text-white">Créer un avoir</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-zinc-300">
                          Créer un avoir pour la facture <strong className="text-red-500">{creditNoteInvoice.invoice_number}</strong> ?
                        </p>
                        <div className="bg-zinc-800 rounded-lg p-4">
                          <p className="text-zinc-400">Client: {creditNoteInvoice.user_name}</p>
                          <p className="text-zinc-400">Montant: <span className="text-red-500 font-bold">-{creditNoteInvoice.total.toFixed(2)} €</span></p>
                        </div>
                        <p className="text-zinc-500 text-sm">L'avoir annulera le montant de la facture originale.</p>
                        <div className="flex gap-3 justify-end">
                          <Button variant="outline" className="border-zinc-600" onClick={() => { setShowCreditNoteForm(false); setCreditNoteInvoice(null); }}>
                            Annuler
                          </Button>
                          <Button className="bg-orange-600 hover:bg-orange-700" onClick={createCreditNote}>
                            Créer l'avoir
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Invoice Form */}
                {showInvoiceForm && (
                  <Card className="bg-zinc-800/50 border-zinc-700 mb-6">
                    <CardContent className="p-6">
                      <h3 className="text-white font-bold mb-4">{editingInvoice ? 'Modifier la facture' : 'Créer une facture'}</h3>
                      <form onSubmit={editingInvoice ? updateInvoice : createInvoice} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-zinc-300">Nom du client *</Label>
                            <Input
                              required
                              value={invoiceForm.user_name}
                              onChange={(e) => setInvoiceForm({...invoiceForm, user_name: e.target.value})}
                              className="bg-zinc-900 border-zinc-700 text-white mt-1"
                              data-testid="invoice-user-name"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Prénom du client</Label>
                            <Input
                              value={invoiceForm.user_firstname}
                              onChange={(e) => setInvoiceForm({...invoiceForm, user_firstname: e.target.value})}
                              className="bg-zinc-900 border-zinc-700 text-white mt-1"
                              data-testid="invoice-user-firstname"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Société</Label>
                            <Input
                              value={invoiceForm.user_company}
                              onChange={(e) => setInvoiceForm({...invoiceForm, user_company: e.target.value})}
                              className="bg-zinc-900 border-zinc-700 text-white mt-1"
                              placeholder="Nom de la société (si applicable)"
                              data-testid="invoice-user-company"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Email *</Label>
                            <Input
                              required
                              type="email"
                              value={invoiceForm.user_email}
                              onChange={(e) => setInvoiceForm({...invoiceForm, user_email: e.target.value})}
                              className="bg-zinc-900 border-zinc-700 text-white mt-1"
                              data-testid="invoice-user-email"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Téléphone</Label>
                            <Input
                              value={invoiceForm.user_phone}
                              onChange={(e) => setInvoiceForm({...invoiceForm, user_phone: e.target.value})}
                              className="bg-zinc-900 border-zinc-700 text-white mt-1"
                              data-testid="invoice-user-phone"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-300">Date d'échéance</Label>
                            <Input
                              type="date"
                              value={invoiceForm.due_date}
                              onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})}
                              className="bg-zinc-900 border-zinc-700 text-white mt-1"
                              data-testid="invoice-due-date"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-zinc-300">Adresse</Label>
                          <Textarea
                            value={invoiceForm.user_address}
                            onChange={(e) => setInvoiceForm({...invoiceForm, user_address: e.target.value})}
                            className="bg-zinc-900 border-zinc-700 text-white mt-1"
                            rows={2}
                          />
                        </div>

                        {/* Items */}
                        <div>
                          <Label className="text-zinc-300 mb-2 block">Articles</Label>
                          <div className="grid grid-cols-12 gap-2 mb-2 text-zinc-400 text-xs">
                            <span className="col-span-4">Description</span>
                            <span className="col-span-1 text-center">Qté</span>
                            <span className="col-span-2 text-center">P.U. HT</span>
                            <span className="col-span-2 text-center">P.U. TTC</span>
                            <span className="col-span-2 text-center">Total HT</span>
                            <span className="col-span-1"></span>
                          </div>
                          {invoiceForm.items.map((item, idx) => {
                            const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
                            const priceHT = typeof item.unit_price === 'string' 
                              ? parseFloat(item.unit_price.replace(',', '.')) || 0 
                              : item.unit_price;
                            const priceTTC = priceHT * 1.2;
                            const totalHT = qty * priceHT;
                            return (
                              <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
                                <Input
                                  placeholder="Description"
                                  value={item.description}
                                  onChange={(e) => updateInvoiceItem(idx, 'description', e.target.value)}
                                  className="bg-zinc-900 border-zinc-700 text-white col-span-4"
                                />
                                <Input
                                  type="number"
                                  placeholder="Qté"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => updateInvoiceItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                                  className="bg-zinc-900 border-zinc-700 text-white col-span-1 text-center"
                                />
                                <Input
                                  type="text"
                                  placeholder="0.00"
                                  inputMode="decimal"
                                  value={item.unit_price}
                                  onChange={(e) => updateInvoiceItem(idx, 'unit_price', e.target.value)}
                                  className="bg-zinc-900 border-zinc-700 text-white col-span-2 text-center"
                                />
                                <div className="col-span-2 text-center text-zinc-400 text-sm">
                                  {priceTTC.toFixed(2)} €
                                </div>
                                <div className="col-span-2 text-center text-white font-medium">
                                  {totalHT.toFixed(2)} €
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeInvoiceItem(idx)}
                                  className="col-span-1 text-red-500 hover:text-red-400"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            );
                          })}
                          <Button type="button" variant="outline" size="sm" onClick={addInvoiceItem} className="mt-2">
                            <Plus size={14} className="mr-1" /> Ajouter une ligne
                          </Button>
                        </div>

                        {/* Totals */}
                        <div className="bg-zinc-900 rounded-lg p-4 mt-4">
                          <div className="flex justify-between text-zinc-400 mb-2">
                            <span>Sous-total HT</span>
                            <span>{calculateInvoiceTotals().subtotal.toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-zinc-400 mb-2">
                            <span>TVA (20%)</span>
                            <span>{calculateInvoiceTotals().taxAmount.toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-white font-bold text-lg border-t border-zinc-700 pt-2">
                            <span>Total TTC</span>
                            <span className="text-green-500">{calculateInvoiceTotals().total.toFixed(2)} €</span>
                          </div>
                        </div>

                        <div>
                          <Label className="text-zinc-300">Notes</Label>
                          <Textarea
                            value={invoiceForm.notes}
                            onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                            className="bg-zinc-900 border-zinc-700 text-white mt-1"
                            rows={2}
                            placeholder="Notes ou conditions particulières..."
                          />
                        </div>

                        <div className="flex gap-3">
                          <Button type="submit" className="bg-green-600 hover:bg-green-700">
                            Créer la facture
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setShowInvoiceForm(false)}>
                            Annuler
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {/* Invoices List */}
                <div className="space-y-4">
                  {sortByTreatment(invoices).map((invoice) => (
                    <Card key={invoice.id} className="bg-zinc-800/30 border-zinc-700">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h4 className="text-white font-bold">{invoice.invoice_number}</h4>
                              <Badge className={statusColors[invoice.status]}>{statusLabels[invoice.status]}</Badge>
                              <DossierAssignmentTag
                                dossierType="invoice"
                                dossierId={invoice.id}
                                assignedTo={invoice.assigned_to}
                                compact
                              />
                            </div>
                            <p className="text-zinc-300 font-medium">{invoice.user_name}</p>
                            <p className="text-zinc-500 text-sm">{invoice.user_email}</p>
                            <div className="mt-3 flex items-center gap-4">
                              <span className="text-green-500 font-bold text-xl">{invoice.total.toFixed(2)} €</span>
                              <span className="text-zinc-500 text-sm">
                                Créée le {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                              </span>
                              {invoice.due_date && (
                                <span className="text-zinc-500 text-sm">
                                  Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                            {/* Items preview */}
                            <div className="mt-3 text-zinc-400 text-sm">
                              {invoice.items.slice(0, 2).map((item, i) => (
                                <div key={i}>{item.description} x{item.quantity} = {item.total.toFixed(2)} €</div>
                              ))}
                              {invoice.items.length > 2 && <div>+ {invoice.items.length - 2} autre(s) article(s)</div>}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 ml-4 items-start">
                            {/* Real PDF download via backend ReportLab */}
                            <Button 
                              size="sm"
                              variant="outline"
                              className="border-green-600 text-green-400"
                              onClick={() => downloadInvoicePdf(invoice)}
                              title="Télécharger la facture en PDF"
                              data-testid={`download-invoice-pdf-${invoice.id}`}
                            >
                              <Download size={14} className="mr-1" /> PDF
                            </Button>
                            {/* Print HTML version (legacy / quick print) */}
                            <Button 
                              size="sm"
                              variant="outline"
                              className="border-zinc-600"
                              onClick={() => downloadInvoice(invoice)}
                              title="Imprimer (aperçu HTML)"
                            >
                              <Printer size={14} className="mr-1" /> Imprimer
                            </Button>
                            {/* Edit Button - visible for all statuses except cancelled */}
                            {invoice.status !== 'cancelled' && (
                              <Button 
                                size="sm"
                                variant="outline"
                                className="border-yellow-600 text-yellow-500"
                                onClick={() => startEditInvoice(invoice)}
                                title="Modifier la facture"
                              >
                                <Edit size={14} className="mr-1" /> Modifier
                              </Button>
                            )}
                            {/* Convert to Quote - visible for pending and sent */}
                            {(invoice.status === 'sent' || invoice.status === 'pending') && (
                              <Button 
                                size="sm"
                                variant="outline"
                                className="border-orange-600 text-orange-500"
                                onClick={() => convertInvoiceToQuote(invoice)}
                                title="Convertir en devis"
                              >
                                <RotateCcw size={14} className="mr-1" /> Devis
                              </Button>
                            )}
                            {/* Delete - always visible */}
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteInvoice(invoice.id)}
                            >
                              <Trash2 size={14} className="mr-1" /> Supprimer
                            </Button>
                            {invoice.user_email && invoice.status !== 'cancelled' && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => sendInvoiceToClient(invoice)}
                                title="Envoyer la facture au client par email (PDF en pièce jointe)"
                                data-testid={`send-invoice-${invoice.id}`}
                              >
                                <Mail size={14} className="mr-1" /> Envoyer au client
                              </Button>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'pending') && (
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => updateInvoiceStatus(invoice.id, 'paid')}
                              >
                                <Check size={14} className="mr-1" /> Marquer payée
                              </Button>
                            )}
                            {invoice.status === 'paid' && invoice.paid_date && (
                              <span className="text-green-500 text-sm">
                                Payée le {new Date(invoice.paid_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {invoices.length === 0 && (
                    <p className="text-center py-8 text-zinc-500">Aucune facture</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CA Reports */}
          <TabsContent value="reports">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp size={24} className="text-green-500" />
                  Rapports Chiffre d'Affaires
                </CardTitle>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Report Type Selector */}
                  <select
                    value={reportType}
                    onChange={(e) => { setReportType(e.target.value); setReportData(null); }}
                    className="bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                  >
                    <option value="daily">Journalier</option>
                    <option value="monthly">Mensuel</option>
                    <option value="yearly">Annuel</option>
                  </select>
                  
                  {/* Date/Month/Year Picker */}
                  {reportType === 'daily' && (
                    <Input
                      type="date"
                      value={reportDate}
                      onChange={(e) => setReportDate(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white w-40"
                    />
                  )}
                  {reportType === 'monthly' && (
                    <Input
                      type="month"
                      value={reportMonth}
                      onChange={(e) => setReportMonth(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white w-40"
                    />
                  )}
                  {reportType === 'yearly' && (
                    <select
                      value={reportYear}
                      onChange={(e) => setReportYear(e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                    >
                      {[2024, 2025, 2026, 2027].map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  )}
                  
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => fetchCAReport(reportType)}
                    disabled={loadingReport}
                  >
                    {loadingReport ? 'Chargement...' : 'Charger le rapport'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {reportData ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-gradient-to-br from-green-600 to-green-800 border-0">
                        <CardContent className="p-4 text-center">
                          <p className="text-green-100 text-sm">CA Total</p>
                          <p className="text-3xl font-bold text-white">
                            {(reportType === 'yearly' ? reportData.grand_total : reportData.total_sales)?.toFixed(2)} €
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-zinc-800 border-zinc-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-zinc-400 text-sm">Transactions</p>
                          <p className="text-2xl font-bold text-white">{reportData.sales_count || 0}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-zinc-800 border-zinc-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-zinc-400 text-sm">Panier moyen</p>
                          <p className="text-2xl font-bold text-white">{reportData.average_sale?.toFixed(2) || '0.00'} €</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-zinc-800 border-zinc-700">
                        <CardContent className="p-4 text-center">
                          <p className="text-zinc-400 text-sm">Annulations</p>
                          <p className="text-2xl font-bold text-red-400">{reportData.cancelled_count || 0}</p>
                          <p className="text-xs text-zinc-500">{reportData.cancelled_total?.toFixed(2) || '0.00'} €</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Payment Breakdown */}
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardHeader>
                        <CardTitle className="text-white text-lg">Répartition par mode de paiement</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-4 gap-4">
                          <div className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                            <Banknote className="text-green-500" size={24} />
                            <div>
                              <p className="text-zinc-400 text-xs">Espèces</p>
                              <p className="text-white font-bold">{reportData.total_cash?.toFixed(2) || '0.00'} €</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                            <CreditCard className="text-blue-500" size={24} />
                            <div>
                              <p className="text-zinc-400 text-xs">Carte bancaire</p>
                              <p className="text-white font-bold">{reportData.total_card?.toFixed(2) || '0.00'} €</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                            <FileText className="text-yellow-500" size={24} />
                            <div>
                              <p className="text-zinc-400 text-xs">Chèques</p>
                              <p className="text-white font-bold">{reportData.total_check?.toFixed(2) || '0.00'} €</p>
                            </div>
                          </div>
                          {reportType === 'yearly' && (
                            <div className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                              <Receipt className="text-purple-500" size={24} />
                              <div>
                                <p className="text-zinc-400 text-xs">Factures hors caisse</p>
                                <p className="text-white font-bold">{reportData.total_invoices?.toFixed(2) || '0.00'} €</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Breakdown Table */}
                    {((reportType === 'daily' && reportData.sales?.length > 0) ||
                      (reportType === 'monthly' && reportData.daily_breakdown?.length > 0) ||
                      (reportType === 'yearly' && reportData.monthly_breakdown?.length > 0)) && (
                      <Card className="bg-zinc-800/50 border-zinc-700">
                        <CardHeader>
                          <CardTitle className="text-white text-lg">
                            {reportType === 'daily' ? 'Détail des ventes' : reportType === 'monthly' ? 'CA par jour' : 'CA par mois'}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-zinc-700">
                                  <th className="text-left text-zinc-400 p-3 text-sm">
                                    {reportType === 'daily' ? 'N° Vente' : reportType === 'monthly' ? 'Date' : 'Mois'}
                                  </th>
                                  <th className="text-left text-zinc-400 p-3 text-sm">
                                    {reportType === 'daily' ? 'Client' : 'Nb ventes'}
                                  </th>
                                  {reportType === 'daily' && <th className="text-left text-zinc-400 p-3 text-sm">Paiement</th>}
                                  {reportType === 'yearly' && <th className="text-left text-zinc-400 p-3 text-sm">Détail</th>}
                                  <th className="text-right text-zinc-400 p-3 text-sm">Montant</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reportType === 'daily' && reportData.sales?.map((sale, idx) => (
                                  <tr key={idx} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                                    <td className="p-3 text-white font-mono text-sm">{sale.sale_number}</td>
                                    <td className="p-3 text-zinc-300">{sale.client_name || 'N/A'}</td>
                                    <td className="p-3">
                                      <Badge className={
                                        sale.payment_method === 'cash' ? 'bg-green-600' :
                                        sale.payment_method === 'card' ? 'bg-blue-600' :
                                        sale.payment_method === 'check' ? 'bg-yellow-600' : 'bg-purple-600'
                                      }>
                                        {sale.payment_method === 'cash' ? 'Espèces' :
                                         sale.payment_method === 'card' ? 'CB' :
                                         sale.payment_method === 'check' ? 'Chèque' : 'Mixte'}
                                      </Badge>
                                    </td>
                                    <td className="p-3 text-right text-white font-bold">{sale.total?.toFixed(2)} €</td>
                                  </tr>
                                ))}
                                {reportType === 'monthly' && reportData.daily_breakdown?.map((day, idx) => (
                                  <tr key={idx} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                                    <td className="p-3 text-white">{new Date(day.date).toLocaleDateString('fr-FR')}</td>
                                    <td className="p-3 text-zinc-300">{day.count}</td>
                                    <td className="p-3 text-right text-white font-bold">{day.total?.toFixed(2)} €</td>
                                  </tr>
                                ))}
                                {reportType === 'yearly' && reportData.monthly_breakdown?.map((m, idx) => {
                                  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                                  const monthNum = parseInt(m.month.split('-')[1]) - 1;
                                  return (
                                    <tr key={idx} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                                      <td className="p-3 text-white font-medium">{monthNames[monthNum]}</td>
                                      <td className="p-3 text-zinc-300">{m.count}</td>
                                      <td className="p-3 text-zinc-400 text-sm">
                                        💵 {m.cash?.toFixed(0)}€ &nbsp; 💳 {m.card?.toFixed(0)}€ &nbsp; 📝 {m.check?.toFixed(0)}€
                                      </td>
                                      <td className="p-3 text-right text-white font-bold">{m.total?.toFixed(2)} €</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Export/Print Buttons */}
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" className="border-zinc-600" onClick={exportReportCSV}>
                        <Download size={16} className="mr-2" />
                        Export CSV
                      </Button>
                      <Button className="bg-blue-600 hover:bg-blue-700" onClick={printCAReport}>
                        <Printer size={16} className="mr-2" />
                        Imprimer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <TrendingUp size={48} className="mx-auto text-zinc-600 mb-4" />
                    <p className="text-zinc-500 mb-4">Sélectionnez une période et cliquez sur "Charger le rapport"</p>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => fetchCAReport(reportType)}
                    >
                      Charger le rapport {reportType === 'daily' ? 'journalier' : reportType === 'monthly' ? 'mensuel' : 'annuel'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Messages de contact</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sortByTreatment(messages).map((msg) => (
                    <Card 
                      key={msg.id} 
                      className={`border ${msg.status === 'unread' ? 'border-red-500/50 bg-red-500/5' : 'border-zinc-700 bg-zinc-800/30'}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h4 className="text-white font-bold">{msg.name}</h4>
                              <Badge className={statusColors[msg.status] || 'bg-zinc-600'}>
                                {msg.status === 'unread' ? 'Non lu' : msg.status === 'replied' ? 'Répondu' : 'Lu'}
                              </Badge>
                              <DossierAssignmentTag
                                dossierType="contact"
                                dossierId={msg.id}
                                assignedTo={msg.assigned_to}
                                compact
                              />
                              {msg.last_replied_by && (
                                <span className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                                  Traité par {msg.last_replied_by}
                                </span>
                              )}
                            </div>
                            <p className="text-zinc-400">{msg.email} • {msg.phone}</p>
                            <p className="text-red-400 font-medium mt-2">{msg.subject}</p>
                            <p className="text-zinc-300 mt-2 whitespace-pre-wrap">{msg.message}</p>
                            <p className="text-zinc-600 text-sm mt-2">
                              {new Date(msg.created_at).toLocaleString('fr-FR')}
                            </p>

                            {/* Replies history */}
                            {Array.isArray(msg.replies) && msg.replies.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {msg.replies.map((r) => (
                                  <div key={r.id} className="bg-emerald-500/5 border-l-2 border-emerald-500 p-3 rounded">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap text-xs">
                                      <span className="text-emerald-400 font-semibold">Réponse de {r.replied_by}</span>
                                      <span className="text-zinc-500">{new Date(r.created_at).toLocaleString('fr-FR')}</span>
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${r.email_sent ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                                        {r.email_sent ? '✓ Email envoyé' : '⚠ Email non délivré'}
                                      </span>
                                    </div>
                                    <p className="text-zinc-300 text-sm whitespace-pre-wrap">{r.body}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => setReplyMessage(msg)}
                              title="Répondre par email"
                              data-testid={`reply-msg-${msg.id}`}
                            >
                              <Send size={14} className="mr-1" /> Répondre
                            </Button>
                            {/* Download message as PDF */}
                            <Button 
                              size="sm"
                              variant="outline"
                              className="border-blue-600 text-blue-400"
                              onClick={() => printContactMessage(msg)}
                              title="Télécharger fiche message"
                            >
                              <Download size={14} className="mr-1" /> Fiche
                            </Button>
                            {msg.status === 'unread' && (
                              <Button 
                                size="sm"
                                variant="outline"
                                className="border-zinc-700"
                                onClick={() => markMessageRead(msg.id)}
                              >
                                <Eye size={14} className="mr-1" /> Lu
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {messages.length === 0 && (
                    <p className="text-center py-8 text-zinc-500">Aucun message</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="text-purple-500" size={20} />
                  Fichier Clients
                </CardTitle>
                <Button 
                  onClick={() => { resetClientForm(); setShowClientForm(true); }}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="new-client-btn"
                >
                  <Plus size={16} className="mr-2" /> Nouveau client
                </Button>
              </CardHeader>
              <CardContent>
                {/* Search bar */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                    <Input
                      placeholder="Rechercher par nom, email, société, téléphone..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white pl-10"
                      data-testid="client-search"
                    />
                  </div>
                </div>

                {/* Client Form Modal */}
                {showClientForm && (
                  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <Card className="bg-zinc-900 border-zinc-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                      <CardHeader>
                        <CardTitle className="text-white">
                          {editingClient ? 'Modifier le client' : 'Nouveau client'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={saveClient} className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-zinc-300">Nom *</Label>
                              <Input
                                required
                                value={clientForm.name}
                                onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                data-testid="client-name"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300">Prénom</Label>
                              <Input
                                value={clientForm.firstname}
                                onChange={(e) => setClientForm({...clientForm, firstname: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                data-testid="client-firstname"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300">Société</Label>
                              <Input
                                value={clientForm.company}
                                onChange={(e) => setClientForm({...clientForm, company: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                placeholder="Nom de la société (si professionnel)"
                                data-testid="client-company"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300">Email *</Label>
                              <Input
                                required
                                type="email"
                                value={clientForm.email}
                                onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                disabled={!!editingClient}
                                data-testid="client-email"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300">Téléphone</Label>
                              <Input
                                value={clientForm.phone}
                                onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                placeholder="06 XX XX XX XX"
                                data-testid="client-phone"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300">Date de naissance</Label>
                              <Input
                                type="date"
                                value={clientForm.birth_date}
                                onChange={(e) => setClientForm({...clientForm, birth_date: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                data-testid="client-birthdate"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-300">Adresse</Label>
                            <Input
                              value={clientForm.address}
                              onChange={(e) => setClientForm({...clientForm, address: e.target.value})}
                              className="bg-zinc-800 border-zinc-700 text-white mt-1"
                              placeholder="Numéro et rue"
                              data-testid="client-address"
                            />
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-zinc-300">Code postal</Label>
                              <Input
                                value={clientForm.postal_code}
                                onChange={(e) => setClientForm({...clientForm, postal_code: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                placeholder="75016"
                                data-testid="client-postalcode"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300">Ville</Label>
                              <Input
                                value={clientForm.city}
                                onChange={(e) => setClientForm({...clientForm, city: e.target.value})}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                                placeholder="Paris"
                                data-testid="client-city"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-300">Notes internes</Label>
                            <Textarea
                              value={clientForm.notes}
                              onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                              className="bg-zinc-800 border-zinc-700 text-white mt-1"
                              placeholder="Notes sur le client (visible uniquement par l'admin)"
                              rows={2}
                              data-testid="client-notes"
                            />
                          </div>
                          <div className="flex gap-3 pt-2">
                            <Button type="submit" className="bg-green-600 hover:bg-green-700">
                              {editingClient ? 'Enregistrer' : 'Créer le client'}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => { setShowClientForm(false); resetClientForm(); }}>
                              Annuler
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Clients table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800 text-zinc-400 text-sm">
                        <th className="text-left py-3 px-2">Client</th>
                        <th className="text-left py-3 px-2">Société</th>
                        <th className="text-left py-3 px-2">Contact</th>
                        <th className="text-left py-3 px-2">Adresse</th>
                        <th className="text-left py-3 px-2">Inscrit le</th>
                        <th className="text-left py-3 px-2">Fidélité</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((client) => (
                        <tr key={client.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="py-3 px-2">
                            <div>
                              <p className="text-white font-medium">
                                {client.firstname ? `${client.firstname} ` : ''}{client.name}
                              </p>
                              {client.birth_date && (
                                <p className="text-zinc-500 text-xs">
                                  Né(e) le {new Date(client.birth_date).toLocaleDateString('fr-FR')}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <p className="text-zinc-300">{client.company || '-'}</p>
                          </td>
                          <td className="py-3 px-2">
                            <p className="text-zinc-300 text-sm">{client.email}</p>
                            <p className="text-zinc-500 text-sm">{client.phone || '-'}</p>
                          </td>
                          <td className="py-3 px-2">
                            <p className="text-zinc-400 text-sm">
                              {client.address ? (
                                <>
                                  {client.address}<br/>
                                  {client.postal_code} {client.city}
                                </>
                              ) : '-'}
                            </p>
                          </td>
                          <td className="py-3 px-2">
                            <p className="text-zinc-300 text-sm">
                              {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '-'}
                            </p>
                          </td>
                          <td className="py-3 px-2">
                            <Badge className="bg-purple-600">
                              {client.loyalty_points || 0} pts
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-zinc-700"
                                onClick={() => openEditClient(client)}
                                title="Voir/Modifier"
                                data-testid={`edit-client-${client.id}`}
                              >
                                <Eye size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
                                onClick={() => openMessageModal(client)}
                                title="Envoyer un message"
                                data-testid={`message-client-${client.id}`}
                              >
                                <MessageCircle size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-600 text-green-400 hover:bg-green-600/20"
                                onClick={() => openWhatsApp(client)}
                                title="WhatsApp"
                                disabled={!client.phone}
                                data-testid={`whatsapp-client-${client.id}`}
                              >
                                <Phone size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-violet-600 text-violet-400"
                                onClick={() => openClientHistory(client.id)}
                                title="Voir tout l'historique"
                                data-testid={`history-client-${client.id}`}
                              >
                                <Eye size={14} className="mr-1" />
                                Historique
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-zinc-700"
                                onClick={() => setResetEmail(client.email)}
                                title="Réinitialiser mot de passe"
                              >
                                <KeyRound size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteClient(client.id)}
                                title="Supprimer"
                                data-testid={`delete-client-${client.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <p className="text-center py-8 text-zinc-500">
                      {clientSearch ? 'Aucun client trouvé' : 'Aucun client enregistré'}
                    </p>
                  )}
                </div>

                {/* Reset Password Section */}
                {resetEmail && (
                  <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <KeyRound className="text-yellow-500" size={18} />
                      Réinitialiser le mot de passe pour {resetEmail}
                    </h4>
                    <form onSubmit={resetUserPassword} className="flex gap-3">
                      <Input
                        type="text"
                        placeholder="Nouveau mot de passe (min. 6 caractères)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-white flex-1"
                        required
                      />
                      <Button type="submit" className="bg-yellow-600 hover:bg-yellow-700" disabled={resetLoading}>
                        {resetLoading ? '...' : 'Réinitialiser'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => { setResetEmail(''); setNewPassword(''); }}>
                        Annuler
                      </Button>
                    </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Birthday Auto-Messages */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    🎂 Messages d'anniversaire automatiques
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <span className="text-zinc-300">Activer les messages d'anniversaire</span>
                    <Button
                      size="sm"
                      variant={birthdaySettings.enabled ? 'default' : 'outline'}
                      className={birthdaySettings.enabled ? 'bg-green-600' : 'border-zinc-700'}
                      onClick={() => setBirthdaySettings({...birthdaySettings, enabled: !birthdaySettings.enabled})}
                    >
                      {birthdaySettings.enabled ? 'Activé' : 'Désactivé'}
                    </Button>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Objet du message</Label>
                    <Input
                      value={birthdaySettings.subject}
                      onChange={(e) => setBirthdaySettings({...birthdaySettings, subject: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Message (utilisez {'{prenom}'} et {'{nom}'})</Label>
                    <Textarea
                      value={birthdaySettings.message}
                      onChange={(e) => setBirthdaySettings({...birthdaySettings, message: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      rows={5}
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button onClick={saveBirthdaySettings} className="bg-green-600 hover:bg-green-700">
                      Sauvegarder
                    </Button>
                    <Button onClick={testBirthdayCheck} variant="outline" className="border-zinc-700">
                      Tester maintenant
                    </Button>
                  </div>
                  
                  <p className="text-xs text-zinc-500">
                    Les messages sont envoyés automatiquement chaque jour à 8h00 aux clients dont c'est l'anniversaire.
                  </p>
                </CardContent>
              </Card>

              {/* Broadcast Messages */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    📣 Envoi en masse (Promotions/Offres)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={sendBroadcast} className="space-y-4">
                    <div>
                      <Label className="text-zinc-300">Type d'envoi</Label>
                      <div className="flex gap-2 mt-2">
                        <Button
                          type="button"
                          variant={broadcastForm.message_type === 'internal' ? 'default' : 'outline'}
                          className={broadcastForm.message_type === 'internal' ? 'bg-blue-600' : 'border-zinc-700'}
                          onClick={() => setBroadcastForm({...broadcastForm, message_type: 'internal'})}
                        >
                          <Mail size={14} className="mr-1" /> Message interne
                        </Button>
                        <Button
                          type="button"
                          variant={broadcastForm.message_type === 'sms' ? 'default' : 'outline'}
                          className={broadcastForm.message_type === 'sms' ? 'bg-green-600' : 'border-zinc-700'}
                          onClick={() => setBroadcastForm({...broadcastForm, message_type: 'sms'})}
                        >
                          <Phone size={14} className="mr-1" /> SMS
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-zinc-300">Destinataires</Label>
                      <select
                        value={broadcastForm.filter_type}
                        onChange={(e) => setBroadcastForm({...broadcastForm, filter_type: e.target.value})}
                        className="w-full mt-1 bg-zinc-800 border border-zinc-700 text-white rounded-md p-2"
                      >
                        <option value="all">Tous les clients ({users.length})</option>
                        <option value="with_phone">Clients avec téléphone ({users.filter(u => u.phone).length})</option>
                        <option value="active_last_month">Clients actifs (30 derniers jours)</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label className="text-zinc-300">Objet</Label>
                      <Input
                        value={broadcastForm.subject}
                        onChange={(e) => setBroadcastForm({...broadcastForm, subject: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        placeholder="Ex: 🎉 Offre spéciale -20% cette semaine !"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-300">Message</Label>
                      <Textarea
                        value={broadcastForm.message}
                        onChange={(e) => setBroadcastForm({...broadcastForm, message: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        placeholder="Votre message promotionnel..."
                        rows={4}
                        required
                      />
                      <p className="text-xs text-zinc-500 mt-1">
                        Utilisez {'{prenom}'} et {'{nom}'} pour personnaliser le message.
                      </p>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-red-600 hover:bg-red-700"
                      disabled={sendingBroadcast}
                    >
                      <Send size={16} className="mr-2" />
                      {sendingBroadcast ? 'Envoi en cours...' : `Envoyer à ${broadcastForm.filter_type === 'all' ? users.length : broadcastForm.filter_type === 'with_phone' ? users.filter(u => u.phone).length : '?'} client(s)`}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Broadcast History */}
              <Card className="bg-zinc-900/50 border-zinc-800 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white">📨 Historique des envois en masse</CardTitle>
                </CardHeader>
                <CardContent>
                  {broadcastHistory.length === 0 ? (
                    <p className="text-center py-8 text-zinc-500">Aucun envoi en masse effectué</p>
                  ) : (
                    <div className="space-y-3">
                      {broadcastHistory.map((broadcast, idx) => (
                        <div key={idx} className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="text-white font-medium">{broadcast.subject}</h4>
                              <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{broadcast.message}</p>
                            </div>
                            <div className="text-right">
                              <Badge className={broadcast.message_type === 'sms' ? 'bg-green-600' : 'bg-blue-600'}>
                                {broadcast.message_type === 'sms' ? 'SMS' : 'Interne'}
                              </Badge>
                              <p className="text-zinc-500 text-xs mt-1">
                                {broadcast.recipient_count} destinataire(s)
                              </p>
                            </div>
                          </div>
                          <p className="text-zinc-500 text-xs mt-2">
                            Envoyé le {new Date(broadcast.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })} par {broadcast.sender_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Team Management - Admin Only */}
          {isMainAdmin && (
            <TabsContent value="team">
              <div className="grid gap-6">
                {/* Collaborators List */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Users className="text-blue-400" size={20} />
                      Collaborateurs ({collaborators.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Nom</th>
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Email</th>
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Rôle</th>
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Date création</th>
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {collaborators.map((collab) => (
                            <tr key={collab.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                              <td className="py-3 px-4">
                                <p className="text-white font-medium">{collab.name}</p>
                              </td>
                              <td className="py-3 px-4 text-zinc-300">{collab.email}</td>
                              <td className="py-3 px-4">
                                <Badge className={collab.role === 'admin' ? 'bg-red-600' : 'bg-blue-600'}>
                                  {collab.role === 'admin' ? 'Admin' : 'Collaborateur'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-zinc-400">
                                {new Date(collab.created_at).toLocaleDateString('fr-FR')}
                              </td>
                              <td className="py-3 px-4">
                                {collab.email !== 'admin@lehubatelier.fr' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
                                    onClick={() => resetCollaboratorPassword(collab.id, collab.name)}
                                    title="Réinitialiser le mot de passe"
                                  >
                                    <KeyRound size={14} className="mr-1" />
                                    Réinit. MDP
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Login History */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <LogIn className="text-green-400" size={20} />
                      Historique des connexions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-zinc-900">
                          <tr className="border-b border-zinc-800">
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Collaborateur</th>
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Email</th>
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Date & Heure</th>
                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Adresse IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginHistory.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="py-8 text-center text-zinc-500">
                                Aucune connexion enregistrée
                              </td>
                            </tr>
                          ) : (
                            loginHistory.map((log) => (
                              <tr key={log.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                                <td className="py-3 px-4">
                                  <p className="text-white font-medium">{log.user_name}</p>
                                  <Badge className={log.role === 'admin' ? 'bg-red-600/20 text-red-400' : 'bg-blue-600/20 text-blue-400'} variant="outline">
                                    {log.role === 'admin' ? 'Admin' : 'Collaborateur'}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4 text-zinc-300">{log.user_email}</td>
                                <td className="py-3 px-4">
                                  <p className="text-white">{new Date(log.login_at).toLocaleDateString('fr-FR')}</p>
                                  <p className="text-zinc-500 text-sm">{new Date(log.login_at).toLocaleTimeString('fr-FR')}</p>
                                </td>
                                <td className="py-3 px-4 text-zinc-400 font-mono text-sm">{log.ip_address}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
          {/* DOSSIERS - aggregated cross-collection view, filterable by collaborator/type */}
          <TabsContent value="dossiers">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Tag size={20} className="text-violet-400" />
                  Dossiers — vue centralisée par collaborateur
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Tous les dossiers (RDV, lavages, devis, factures, dépannages, contacts, commandes) avec leur étiquette d&apos;assignation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters */}
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 text-xs">Collaborateur</Label>
                    <select
                      value={dossierFilterCollab}
                      onChange={(e) => { setDossierFilterCollab(e.target.value); fetchDossiers(e.target.value, dossierFilterType); }}
                      className="w-full bg-zinc-800 border border-zinc-700 text-white p-2 rounded mt-1"
                      data-testid="dossier-filter-collab"
                    >
                      <option value="">Tous les collaborateurs</option>
                      <option value="__unassigned__">⚠ Non attribués</option>
                      {['Sabrina', 'Harout', 'Ivan', 'Azdine', 'Sasha', 'Pierre', 'Evan', 'Quentin'].map(n => (
                        <option key={n} value={n}>{n} ({dossiersData?.stats_by_collaborator?.[n] || 0})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-xs">Type de dossier</Label>
                    <select
                      value={dossierFilterType}
                      onChange={(e) => { setDossierFilterType(e.target.value); fetchDossiers(dossierFilterCollab, e.target.value); }}
                      className="w-full bg-zinc-800 border border-zinc-700 text-white p-2 rounded mt-1"
                      data-testid="dossier-filter-type"
                    >
                      <option value="">Tous les types</option>
                      <option value="appointment">Rendez-vous ({dossiersData?.stats_by_type?.appointment || 0})</option>
                      <option value="wash">Lavages ({dossiersData?.stats_by_type?.wash || 0})</option>
                      <option value="towing">Dépannages ({dossiersData?.stats_by_type?.towing || 0})</option>
                      <option value="quote">Demandes de devis ({dossiersData?.stats_by_type?.quote || 0})</option>
                      <option value="admin_quote">Devis pro ({dossiersData?.stats_by_type?.admin_quote || 0})</option>
                      <option value="invoice">Factures ({dossiersData?.stats_by_type?.invoice || 0})</option>
                      <option value="order">Commandes ({dossiersData?.stats_by_type?.order || 0})</option>
                      <option value="contact">Messages contact ({dossiersData?.stats_by_type?.contact || 0})</option>
                    </select>
                  </div>
                </div>

                {/* List */}
                {!dossiersData ? (
                  <div className="text-center py-12 text-zinc-500">Chargement des dossiers...</div>
                ) : dossiersData.items.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">Aucun dossier pour ces critères</div>
                ) : (
                  <div className="space-y-2">
                    {sortByTreatment(dossiersData.items).map((item) => {
                      const typeLabels = {
                        appointment: { label: 'RDV', color: 'bg-blue-600' },
                        wash: { label: 'Lavage', color: 'bg-cyan-600' },
                        towing: { label: 'Dépannage', color: 'bg-orange-600' },
                        quote: { label: 'Demande devis', color: 'bg-purple-600' },
                        admin_quote: { label: 'Devis pro', color: 'bg-fuchsia-600' },
                        invoice: { label: 'Facture', color: 'bg-emerald-600' },
                        order: { label: 'Commande', color: 'bg-amber-600' },
                        contact: { label: 'Message', color: 'bg-yellow-600' },
                      };
                      const tConf = typeLabels[item._dossier_type] || { label: item._dossier_type, color: 'bg-zinc-600' };
                      const clientLabel = item.client_name || item.user_name || item.name || `${item.firstname || ''} ${item.last_name || ''}`.trim() || item.email || '-';
                      const subtitle = item.service_type || item.wash_type || item.subject || item.invoice_number || item.quote_number || item.intervention_type || '';
                      const total = (typeof item.total === 'number') ? `${item.total.toFixed(2)} €` : (item.price ? `${Number(item.price).toFixed(2)} €` : '');
                      const date = item.created_at ? new Date(item.created_at).toLocaleString('fr-FR') : '';
                      return (
                        <div key={`${item._dossier_type}-${item.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/40 hover:bg-zinc-800 border border-zinc-700">
                          <Badge className={`${tConf.color} text-white shrink-0`}>{tConf.label}</Badge>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-medium truncate">{clientLabel}</span>
                              {subtitle && <span className="text-zinc-400 text-sm truncate">— {subtitle}</span>}
                              {total && <span className="text-emerald-400 font-bold ml-auto shrink-0">{total}</span>}
                            </div>
                            <p className="text-xs text-zinc-500">{date}</p>
                          </div>
                          <div className="shrink-0">
                            <DossierAssignmentTag
                              dossierType={item._dossier_type}
                              dossierId={item.id}
                              assignedTo={item.assigned_to}
                              compact
                              onChange={() => fetchDossiers(dossierFilterCollab, dossierFilterType)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        {/* Modal Détails Client */}
        {selectedUserForDetails && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUserForDetails(null)}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="sticky top-0 bg-zinc-900 border-b border-zinc-700 p-6 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {selectedUserForDetails.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedUserForDetails.name}</h2>
                    <p className="text-zinc-400">{selectedUserForDetails.email}</p>
                    {selectedUserForDetails.phone && <p className="text-zinc-500 text-sm">{selectedUserForDetails.phone}</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-blue-600 text-blue-500"
                    onClick={() => {
                      printAppointmentClientSheet(
                        { 
                          user_id: selectedUserForDetails.id,
                          user_name: selectedUserForDetails.name,
                          user_email: selectedUserForDetails.email,
                          user_phone: selectedUserForDetails.phone,
                          date: new Date().toISOString(),
                          service_name: 'Consultation fiche',
                          status: 'completed'
                        },
                        selectedUserForDetails.history
                      );
                    }}
                  >
                    <Printer size={16} className="mr-2" />
                    Imprimer fiche
                  </Button>
                  <Button variant="ghost" className="text-zinc-400" onClick={() => setSelectedUserForDetails(null)}>
                    <X size={20} />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Stats rapides */}
                {selectedUserForDetails.history?.stats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardContent className="p-4 text-center">
                        <Euro className="mx-auto text-green-500 mb-2" size={24} />
                        <p className="text-2xl font-bold text-white">{selectedUserForDetails.history.stats.total_spent?.toFixed(2) || 0}€</p>
                        <p className="text-zinc-500 text-sm">Total dépensé</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardContent className="p-4 text-center">
                        <Calendar className="mx-auto text-blue-500 mb-2" size={24} />
                        <p className="text-2xl font-bold text-white">{selectedUserForDetails.history.appointments?.length || 0}</p>
                        <p className="text-zinc-500 text-sm">Rendez-vous</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardContent className="p-4 text-center">
                        <Receipt className="mx-auto text-purple-500 mb-2" size={24} />
                        <p className="text-2xl font-bold text-white">{selectedUserForDetails.history.sales?.length || 0}</p>
                        <p className="text-zinc-500 text-sm">Achats en caisse</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardContent className="p-4 text-center">
                        <FileText className="mx-auto text-orange-500 mb-2" size={24} />
                        <p className="text-2xl font-bold text-white">{selectedUserForDetails.history.invoices?.length || 0}</p>
                        <p className="text-zinc-500 text-sm">Factures</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Historique des RDV */}
                {selectedUserForDetails.history?.appointments && selectedUserForDetails.history.appointments.length > 0 && (
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Calendar className="text-blue-400" size={20} />
                        Historique des rendez-vous ({selectedUserForDetails.history.appointments.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-zinc-700">
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Date</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Service</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Véhicule</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedUserForDetails.history.appointments.slice(0, 10).map((apt, idx) => (
                              <tr key={idx} className="border-b border-zinc-800/50">
                                <td className="py-2 px-3 text-white">{new Date(apt.date).toLocaleDateString('fr-FR')}</td>
                                <td className="py-2 px-3 text-zinc-300">{apt.service_name || '-'}</td>
                                <td className="py-2 px-3 text-zinc-400">{apt.vehicle_brand} {apt.vehicle_model} {apt.vehicle_plate && `(${apt.vehicle_plate})`}</td>
                                <td className="py-2 px-3">
                                  <Badge className={statusColors[apt.status] || 'bg-gray-500'}>{apt.status}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Historique des ventes en caisse */}
                {selectedUserForDetails.history?.sales && selectedUserForDetails.history.sales.length > 0 && (
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Receipt className="text-green-400" size={20} />
                        Historique des achats en caisse ({selectedUserForDetails.history.sales.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-zinc-700">
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Date</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Prestations</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Montant</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Paiement</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedUserForDetails.history.sales.slice(0, 10).map((sale, idx) => (
                              <tr key={idx} className="border-b border-zinc-800/50">
                                <td className="py-2 px-3 text-white">{new Date(sale.created_at).toLocaleDateString('fr-FR')}</td>
                                <td className="py-2 px-3 text-zinc-300">{sale.items?.map(i => i.description).join(', ') || '-'}</td>
                                <td className="py-2 px-3 text-green-500 font-bold">{sale.total?.toFixed(2) || 0}€</td>
                                <td className="py-2 px-3 text-zinc-400">
                                  {sale.payment_method === 'cash' ? 'Espèces' : sale.payment_method === 'card' ? 'CB' : sale.payment_method === 'transfer' ? 'Virement' : sale.payment_method}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Factures */}
                {selectedUserForDetails.history?.invoices && selectedUserForDetails.history.invoices.length > 0 && (
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <FileText className="text-orange-400" size={20} />
                        Factures ({selectedUserForDetails.history.invoices.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-zinc-700">
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">N° Facture</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Date</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Montant</th>
                              <th className="text-left py-2 px-3 text-zinc-400 text-sm">Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedUserForDetails.history.invoices.slice(0, 10).map((inv, idx) => (
                              <tr key={idx} className="border-b border-zinc-800/50">
                                <td className="py-2 px-3 text-white font-mono">{inv.invoice_number}</td>
                                <td className="py-2 px-3 text-zinc-300">{new Date(inv.created_at).toLocaleDateString('fr-FR')}</td>
                                <td className="py-2 px-3 text-green-500 font-bold">{inv.total?.toFixed(2) || 0}€</td>
                                <td className="py-2 px-3">
                                  <Badge className={inv.status === 'paid' ? 'bg-green-600' : inv.status === 'sent' ? 'bg-blue-600' : 'bg-yellow-600'}>
                                    {inv.status === 'paid' ? 'Payée' : inv.status === 'sent' ? 'Envoyée' : 'En attente'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Message si pas d'historique */}
                {(!selectedUserForDetails.history || 
                  (!selectedUserForDetails.history.appointments?.length && 
                   !selectedUserForDetails.history.sales?.length && 
                   !selectedUserForDetails.history.invoices?.length)) && (
                  <div className="text-center py-12">
                    <Users className="mx-auto text-zinc-600 mb-4" size={48} />
                    <p className="text-zinc-500">Aucun historique disponible pour ce client</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dialog modification libre RDV (admin) */}
        <Dialog open={!!editingApptAdmin} onOpenChange={(open) => !open && setEditingApptAdmin(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-edit-appt-dialog">
            <DialogHeader>
              <DialogTitle>Modifier le rendez-vous</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">Date</Label>
                <Input type="date" value={apptAdminForm.date || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, date: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Heure</Label>
                <Input type="time" value={apptAdminForm.time_slot || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, time_slot: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Service (libre)</Label>
                <Input value={apptAdminForm.service_name || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, service_name: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Marque</Label>
                <Input value={apptAdminForm.vehicle_brand || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, vehicle_brand: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Modele</Label>
                <Input value={apptAdminForm.vehicle_model || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, vehicle_model: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Plaque</Label>
                <Input value={apptAdminForm.vehicle_plate || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, vehicle_plate: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Telephone client</Label>
                <Input value={apptAdminForm.user_phone || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, user_phone: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Statut</Label>
                <Select value={apptAdminForm.status} onValueChange={(v) => setApptAdminForm({ ...apptAdminForm, status: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="confirmed">Confirme</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="completed">Termine</SelectItem>
                    <SelectItem value="cancelled">Annule</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Assigne a</Label>
                <Input value={apptAdminForm.assigned_to || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, assigned_to: e.target.value })} className="bg-zinc-800 border-zinc-700" placeholder="Nom collaborateur" />
              </div>
              <div>
                <Label className="text-zinc-400">Prix (EUR)</Label>
                <Input type="number" step="0.01" value={apptAdminForm.price ?? ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, price: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notes</Label>
                <Textarea value={apptAdminForm.notes || ''} onChange={(e) => setApptAdminForm({ ...apptAdminForm, notes: e.target.value })} className="bg-zinc-800 border-zinc-700" rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingApptAdmin(null)} className="border-zinc-700">Annuler</Button>
              <Button onClick={saveApptAdmin} disabled={apptAdminSaving} className="bg-red-600 hover:bg-red-700" data-testid="admin-save-appt-btn">
                {apptAdminSaving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog modification libre Lavage (admin) */}
        <Dialog open={!!editingWashAdmin} onOpenChange={(open) => !open && setEditingWashAdmin(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-edit-wash-dialog">
            <DialogHeader>
              <DialogTitle>Modifier la reservation lavage</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">Date</Label>
                <Input type="date" value={washAdminForm.date || ''} onChange={(e) => setWashAdminForm({ ...washAdminForm, date: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Creneau</Label>
                <Input type="time" value={washAdminForm.time_slot || ''} onChange={(e) => setWashAdminForm({ ...washAdminForm, time_slot: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Formule</Label>
                <Select value={washAdminForm.wash_type} onValueChange={(v) => setWashAdminForm({ ...washAdminForm, wash_type: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="velo">VELO (12 EUR)</SelectItem>
                    <SelectItem value="clean">CLEAN (25 EUR)</SelectItem>
                    <SelectItem value="vip">VIP (55 EUR)</SelectItem>
                    <SelectItem value="platine">PLATINE (75 EUR)</SelectItem>
                    <SelectItem value="prestige">PRESTIGE (115 EUR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Type vehicule</Label>
                <Select value={washAdminForm.vehicle_type} onValueChange={(v) => setWashAdminForm({ ...washAdminForm, vehicle_type: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="moto">Moto</SelectItem>
                    <SelectItem value="velo">Velo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Vehicule (info libre)</Label>
                <Input value={washAdminForm.vehicle_info || ''} onChange={(e) => setWashAdminForm({ ...washAdminForm, vehicle_info: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Statut</Label>
                <Select value={washAdminForm.status} onValueChange={(v) => setWashAdminForm({ ...washAdminForm, status: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="pending_payment">Paiement attendu</SelectItem>
                    <SelectItem value="confirmed">Confirme</SelectItem>
                    <SelectItem value="completed">Termine</SelectItem>
                    <SelectItem value="cancelled">Annule</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Prix (EUR)</Label>
                <Input type="number" step="0.01" value={washAdminForm.price ?? ''} onChange={(e) => setWashAdminForm({ ...washAdminForm, price: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-zinc-400">Mode paiement</Label>
                <Select value={washAdminForm.payment_method} onValueChange={(v) => setWashAdminForm({ ...washAdminForm, payment_method: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="onsite">Sur place</SelectItem>
                    <SelectItem value="systempay">SystemPay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={!!washAdminForm.paid} onChange={(e) => setWashAdminForm({ ...washAdminForm, paid: e.target.checked })} />
                <Label className="text-zinc-400">Paye</Label>
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notes</Label>
                <Textarea value={washAdminForm.notes || ''} onChange={(e) => setWashAdminForm({ ...washAdminForm, notes: e.target.value })} className="bg-zinc-800 border-zinc-700" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingWashAdmin(null)} className="border-zinc-700">Annuler</Button>
              <Button onClick={saveWashAdmin} disabled={washAdminSaving} className="bg-red-600 hover:bg-red-700" data-testid="admin-save-wash-btn">
                {washAdminSaving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reply to contact message modal */}
        {replyMessage && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => { if (!replySending) { setReplyMessage(null); setReplyBody(''); } }}
            data-testid="reply-modal"
          >
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-zinc-800 flex justify-between items-start">
                <div>
                  <h3 className="text-white font-bold text-lg">Répondre à {replyMessage.name}</h3>
                  <p className="text-zinc-400 text-sm">{replyMessage.email} — {replyMessage.subject}</p>
                  <p className="text-emerald-400 text-xs mt-1">
                    Traité par <strong>{replyMessage.assigned_to || user?.name || 'vous-même'}</strong>
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setReplyMessage(null); setReplyBody(''); }} className="text-zinc-400">
                  <X size={18} />
                </Button>
              </div>
              <div className="p-5">
                <div className="bg-zinc-800/40 p-3 rounded mb-4 border-l-2 border-zinc-600">
                  <p className="text-zinc-400 text-xs mb-1">Message original :</p>
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap">{replyMessage.message}</p>
                </div>
                <Label className="text-zinc-300">Votre réponse</Label>
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  rows={8}
                  className="w-full mt-2 bg-zinc-800 border border-zinc-700 text-white p-3 rounded focus:border-red-500 focus:outline-none"
                  placeholder="Tapez votre réponse — elle sera envoyée par email au client avec votre nom comme signature..."
                  data-testid="reply-body"
                />
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setReplyMessage(null); setReplyBody(''); }} disabled={replySending} className="border-zinc-700">
                    Annuler
                  </Button>
                  <Button onClick={sendReply} disabled={replySending || !replyBody.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reply-send">
                    {replySending ? 'Envoi...' : (<><Send size={14} className="mr-2" /> Envoyer la réponse</>)}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Client history modal */}
        {clientHistory && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setClientHistory(null)}
            data-testid="client-history-modal"
          >
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-zinc-800 flex justify-between items-start sticky top-0 bg-zinc-900 z-10">
                <div>
                  <h3 className="text-white font-bold text-lg">
                    Historique de {clientHistory?.client?.firstname} {clientHistory?.client?.name}
                  </h3>
                  <p className="text-zinc-400 text-sm">{clientHistory?.client?.email} — {clientHistory?.stats?.total || 0} interactions</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setClientHistory(null)} className="text-zinc-400">
                  <X size={18} />
                </Button>
              </div>
              <div className="p-5">
                {clientHistoryLoading ? (
                  <p className="text-center py-12 text-zinc-500">Chargement...</p>
                ) : !clientHistory.items || clientHistory.items.length === 0 ? (
                  <p className="text-center py-12 text-zinc-500">Aucune interaction enregistrée pour ce client.</p>
                ) : (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                      {[
                        { k: 'appointments', label: 'RDV', color: 'bg-blue-600/20 text-blue-300' },
                        { k: 'wash_bookings', label: 'Lavages', color: 'bg-cyan-600/20 text-cyan-300' },
                        { k: 'towing_requests', label: 'Dépannages', color: 'bg-orange-600/20 text-orange-300' },
                        { k: 'quote_requests', label: 'Demandes devis', color: 'bg-purple-600/20 text-purple-300' },
                        { k: 'admin_quotes', label: 'Devis pro', color: 'bg-fuchsia-600/20 text-fuchsia-300' },
                        { k: 'invoices', label: 'Factures', color: 'bg-emerald-600/20 text-emerald-300' },
                        { k: 'orders', label: 'Commandes', color: 'bg-amber-600/20 text-amber-300' },
                        { k: 'contact_messages', label: 'Messages', color: 'bg-yellow-600/20 text-yellow-300' },
                      ].map(s => (
                        <div key={s.k} className={`p-3 rounded ${s.color}`}>
                          <p className="text-xs">{s.label}</p>
                          <p className="text-xl font-bold">{clientHistory.stats?.[s.k] || 0}</p>
                        </div>
                      ))}
                    </div>
                    {/* Timeline */}
                    <div className="space-y-2">
                      {sortByTreatment(clientHistory.items).map((item) => {
                        const typeLabels = {
                          appointment: { label: 'RDV', color: 'bg-blue-600' },
                          wash: { label: 'Lavage', color: 'bg-cyan-600' },
                          towing: { label: 'Dépannage', color: 'bg-orange-600' },
                          quote: { label: 'Demande devis', color: 'bg-purple-600' },
                          admin_quote: { label: 'Devis pro', color: 'bg-fuchsia-600' },
                          invoice: { label: 'Facture', color: 'bg-emerald-600' },
                          order: { label: 'Commande', color: 'bg-amber-600' },
                          contact: { label: 'Message', color: 'bg-yellow-600' },
                        };
                        const tConf = typeLabels[item._history_type] || { label: item._history_type, color: 'bg-zinc-600' };
                        const subtitle = item.service_type || item.wash_type || item.subject || item.invoice_number || item.quote_number || item.intervention_type || '';
                        const total = (typeof item.total === 'number') ? `${item.total.toFixed(2)} €` : '';
                        const status = item.status || '';
                        return (
                          <div key={`${item._history_type}-${item.id}`} className="flex items-start gap-3 p-3 rounded bg-zinc-800/40 border border-zinc-700">
                            <Badge className={`${tConf.color} text-white shrink-0`}>{tConf.label}</Badge>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {subtitle && <span className="text-white font-medium">{subtitle}</span>}
                                {status && <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">{status}</Badge>}
                                {total && <span className="text-emerald-400 font-semibold ml-auto">{total}</span>}
                              </div>
                              {item.message && <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{item.message}</p>}
                              {item.description && <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{item.description}</p>}
                              {item.assigned_to && (
                                <p className="text-xs text-emerald-400 mt-1">🏷️ Traité par {item.assigned_to}</p>
                              )}
                              <p className="text-xs text-zinc-500 mt-1">{item.created_at ? new Date(item.created_at).toLocaleString('fr-FR') : ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
